import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  InputGroup,
  Spinner,
  Card,
  Alert,
} from 'react-bootstrap';
import {
  Search as FiSearch,
  Trash3 as FiTrash2,
  Image as FiImage,
} from 'react-bootstrap-icons';
import { fetchProductById, loadProducts } from '../../api/products';
import {
  loadClients,
  Client,
  getClientById,
  searchSalesPeople,
  SalesPerson,
} from '../../api/clients';
import {
  approveOrderServer,
  createOrderServer,
  getOrderServer,
  modifyOrderServer,
  repairProfitPlusOrderExportServer,
} from '../../api/ordersServer';
import {
  assessOrderProfitSyncEntityEligibility,
  collectProfitOrderSyncAssessment,
  type ProfitOrderSyncAssessment,
} from '../../api/orders';
import {
  loadConfigurationFactors,
  loadProfitPlus2k8IntegrationConfig,
  loadProfitPlus2k8ReferenceTables,
} from '../../api/configuration';
import type {
  ProfitPlus2k8OrgConfig,
  ProfitPlus2k8ReferenceTables,
} from '../../models/configuration';
import type {
  ProfitPlus2k8SaleIntegration,
  ProfitPlusPedidoRecord,
} from '../../models/integrations/profitPlus2k8';
import { listExchangeSourcesForPair } from '../../api/exchangeSources';
import { listAllActiveWarehouses, listStockBalancesForProducts } from '../../api/warehouses';
import { getCurrentAuthUser } from '../../utils/currentAuthUser';
import {
  catalogProductIdFromLine,
  evaluateAggregatedStockGate,
  formatWarehouseStockHint,
  hasInsufficientStockAtWarehouse,
  hasSufficientStockSomewhere,
  isProductStockBalancesLoaded,
  lineSellableWithSharedPool,
  reservedQtyHeldByOrderLines,
  resolveWarehouseWithSufficientSellable,
  sellableAtWarehouse,
  siblingDemandAtWarehouse,
  totalSellableAcrossWarehouses,
} from '../../utils/orderWarehouseStock';
import {
  isMultiWarehouseMode,
  resolveInventoryMode,
} from '../../utils/inventoryMode';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import { useLoading } from '../../hooks/useLoading';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { Product } from '../../models';
import type { Warehouse } from '../../models/warehouse';
import type { StockBalance } from '../../models/stockBalance';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import {
  Order,
  OrderCreate,
  OrderUpdate,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
} from '../../models/order';
import type { ExchangeSourceOption } from '../../models/exchangeSource';
import formStyles from './OrderForm.module.scss';
import { DateField } from '../shared/DateField';
import {
  computeOrderLineSubtotal,
  computeOrderTotals,
  normalizeDiscountPercent,
} from './orderForm/orderFormTotals';
import {
  resolveOrderLineTaxForPersistence,
  resolveProductTaxInfo,
} from './orderForm/orderFormTax';
import {
  buildOrderCommonFields,
  buildOrderProductsPayload,
  resolveCreatorSalesPersonString as buildCreatorSalesPersonString,
  validateOrderFormForSave,
} from './orderForm/orderFormPayload';
import {
  canProceedOrderFormStep1,
  canProceedOrderFormStep2,
} from './orderForm/orderFormSteps';
import {
  creditUiStateFromOrder,
  mapOrderLinesToSelectedProducts,
} from './orderForm/orderFormEdit';
import {
  formatSalesPersonOptionLabel,
  resolveStoredCreatorSalesPersonToVendorId,
} from './orderForm/orderFormSalesPerson';
import {
  applyAuthoritativePedidoFieldsFromServer,
  buildProfitPedidoSummaryLegible,
  buildProfitSyncAssessmentMessage,
  buildSelectedLineStockWarnings,
  buildSubAlmacenOptionsFromReferenceTables,
  collectProfitLineCoSubIssues,
  findProfitUsdMonedaCode,
  getSalesPersonProfitCoVen,
  hasAnySaleProfitPedidoField,
  isMissingProfitVendedorForExport,
  isProfitStep1Ready,
  orderDocumentProfitPlus2k8FromOrder,
  orderLineCoSubFromPayload,
  pickRecommendedCoSubForLine as pickRecommendedCoSubForLineFn,
  resolveAvailableQtyAtCoSub,
  resolveCoSubForAlmacen,
  resolveEffectiveCoSucu,
  resolveMaxAvailableQtyAcrossSubs,
  shouldShowSaleProfitPlusFields,
  subAlmacenOptionsIncludingSavedSelection,
} from '../../integrations/profitPlus2k8';

interface OrderFormProps {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
  organizationId: string;
  order?: Order; // For viewing existing orders
  readOnly?: boolean; // For read-only mode
  addToast: (message: string) => void;
  /** Opened from Orders «Aprobar»: review full sale (and Profit fields when export is configured), then approve. */
  approvalReviewMode?: boolean;
  /** Called after a successful approve from approval review (e.g. start Profit sync poll). */
  onApprovalReviewSuccess?: (orderId: string) => void;
  /**
   * Approved sale not yet exported: only Profit Plus blobs (header + line subalmacén) can be saved.
   * Inventory and commercial lines stay fixed.
   */
  profitSyncRepairMode?: boolean;
  /** After a successful Profit repair save (parent may enqueue sync). */
  onProfitSyncRepairSaved?: (orderId: string) => void;
}

interface SelectedProduct extends Product {
  selectedQuantity: number;
  discount: number;
  customPrice?: number; // Allow custom pricing override
  customTaxId?: string; // Tax override only for this sale line
  selectedCoSub?: string; // Profit sub-almacén seleccionado para esta línea
  warehouseId?: string;
  /**
   * Catalog product id when `id` is a unique line key (duplicate SKUs on edit).
   * Same as Product.id for newly added catalog lines.
   */
  productId?: string;
}

interface TaxConfigItem {
  id: string;
  name: string;
  rate: number;
  enabled: boolean;
}

type ExchangeRateMode = 'saleDefault' | 'orgDefault' | 'source' | 'manual';

const OrderForm: React.FC<OrderFormProps> = ({
  show,
  onClose,
  onSaved,
  organizationId,
  order,
  readOnly = false,
  addToast,
  approvalReviewMode = false,
  onApprovalReviewSuccess,
  profitSyncRepairMode = false,
  onProfitSyncRepairSaved,
}) => {
  const isEditing = Boolean(order && !readOnly);
  const [hydratedApprovalState, setHydratedApprovalState] = useState<
    string | undefined
  >(order?.approvalState);
  const effectiveApprovalState =
    hydratedApprovalState || order?.approvalState;
  const warehouseLocked = Boolean(
    isEditing && effectiveApprovalState === 'approved'
  );
  const isProfitSyncRepair = Boolean(
    profitSyncRepairMode && order && !readOnly
  );
  const originalSaleExchangeRate =
    isEditing && order && typeof order.exchangeRate === 'number'
      ? order.exchangeRate
      : null;
  const { showLoading, hideLoading } = useLoading();
  const { showInfoConfirmation } = useConfirmationDialog();
  const [step, setStep] = useState<number>(1);

  /** While editing quantity, keep raw string so empty / partial decimals don't become 0 → bogus clamps. */
  const [quantityDraftByProductId, setQuantityDraftByProductId] = useState<
    Record<string, string>
  >({});

  /** Same for discount %: allow clearing the field while typing before commit on blur. */
  const [discountDraftByProductId, setDiscountDraftByProductId] = useState<
    Record<string, string>
  >({});

  // Step 1: Products
  const [products, setProducts] = useState<Product[]>([]);
  /**
   * In edit mode we avoid loading the full products catalog automatically
   * (it can be very long). Users can explicitly load it if they want to add
   * new products to the existing sale.
   */
  const [shouldShowProductCatalog, setShouldShowProductCatalog] =
    useState<boolean>(!isEditing);
  const [productSearch, setProductSearch] = useState('');
  const [productPending, setProductPending] = useState(false);
  const [productLastDoc, setProductLastDoc] = useState<any>(null);
  const [productHasMore, setProductHasMore] = useState(false);
  const [productLoadMorePending, setProductLoadMorePending] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );

  // Step 2: Client
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientPending, setClientPending] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showClientMoreInfo, setShowClientMoreInfo] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');

  // Step 3: Salesperson and payment
  const [salesPeople, setSalesPeople] = useState<SalesPerson[]>([]);
  const [vendorPending, setVendorPending] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>(
    PAYMENT_METHOD_LABELS[PAYMENT_METHODS.CASH]
  );
  const [paymentCurrency, setPaymentCurrency] = useState<string>('');
  const [creditEnabled, setCreditEnabled] = useState<boolean>(false);
  const [creditTermsDays, setCreditTermsDays] = useState<string>('');
  const [creditDueDate, setCreditDueDate] = useState<string>('');
  const [emissionDate, setEmissionDate] = useState<string>(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [comment, setComment] = useState('');
  const [orderDiscountPercent, setOrderDiscountPercent] = useState(0);
  /** Keep latest discount for confirm-dialog callbacks (avoid stale closure on save). */
  const orderDiscountPercentRef = useRef(orderDiscountPercent);
  orderDiscountPercentRef.current = orderDiscountPercent;
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    Array<{ id: number; name: string; enabled: boolean }>
  >([]);
  const [currency, setCurrency] = useState<string>('VES');
  const [referenceCurrency, setReferenceCurrency] = useState<string>('USD');
  const [multiWarehouseEnabled, setMultiWarehouseEnabled] = useState(false);
  const [defaultWarehouseId, setDefaultWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [balancesByProductId, setBalancesByProductId] = useState<
    Record<string, StockBalance[]>
  >({});
  const [balancesLoadingProductIds, setBalancesLoadingProductIds] = useState<
    Record<string, boolean>
  >({});

  // Configuration factors for IVA calculation and approval settings
  const [configurationFactors, setConfigurationFactors] = useState<{
    iva: number;
    orderApprovalRequired: boolean;
    allowNegativeInventory: boolean;
    blockSalesWhenInsufficientStock: boolean;
    taxes: TaxConfigItem[];
    primaryTaxId: string;
  }>({
    iva: 16,
    orderApprovalRequired: false,
    allowNegativeInventory: false,
    blockSalesWhenInsufficientStock: false,
    taxes: [],
    primaryTaxId: '',
  });

  // Exchange rate from configuration factors
  const [exchangeRate, setExchangeRate] = useState<number>(36.0);
  const [defaultExchangeRate, setDefaultExchangeRate] = useState<number>(36.0);
  const [defaultExchangeSourceId, setDefaultExchangeSourceId] =
    useState<string>('');
  const [orgUsesAutomaticExchangeRate, setOrgUsesAutomaticExchangeRate] =
    useState<boolean>(false);
  const [exchangeSourceOverrideId, setExchangeSourceOverrideId] =
    useState<string>('');
  const [exchangeRateMode, setExchangeRateMode] =
    useState<ExchangeRateMode>('orgDefault');
  const [exchangeSources, setExchangeSources] = useState<
    ExchangeSourceOption[]
  >([]);
  /** After `fetchConfiguration` finishes (avoids flashing USD / error alerts before data exists). */
  const [profitReferenceDataReady, setProfitReferenceDataReady] =
    useState(false);
  /** Profit Plus 2k8: optional dbo.pedidos overrides for this sale (stored on sale doc). */
  const [profitPlus2k8Config, setProfitPlus2k8Config] =
    useState<ProfitPlus2k8OrgConfig | null>(null);
  const [referenceTables, setReferenceTables] =
    useState<ProfitPlus2k8ReferenceTables | null>(null);
  const [saleProfitFields, setSaleProfitFields] = useState<
    Partial<ProfitPlusPedidoRecord>
  >({});
  const [profitEligibilityIssues, setProfitEligibilityIssues] = useState<
    string[]
  >([]);
  const [profitSyncBlockers, setProfitSyncBlockers] = useState<string[]>([]);
  const [profitSyncWarnings, setProfitSyncWarnings] = useState<string[]>([]);

  const orgAutomaticExchangeSource = useMemo(() => {
    if (!orgUsesAutomaticExchangeRate || !defaultExchangeSourceId) return null;
    return (
      exchangeSources.find(source => source.id === defaultExchangeSourceId) ||
      null
    );
  }, [orgUsesAutomaticExchangeRate, defaultExchangeSourceId, exchangeSources]);

  const orgEffectiveExchangeRate = useMemo(() => {
    if (
      orgAutomaticExchangeSource &&
      typeof orgAutomaticExchangeSource.rate === 'number' &&
      orgAutomaticExchangeSource.rate > 0
    ) {
      return orgAutomaticExchangeSource.rate;
    }
    return defaultExchangeRate;
  }, [orgAutomaticExchangeSource, defaultExchangeRate]);

  // IVA toggle
  const [applyIva, setApplyIva] = useState<boolean>(true);

  const groupedNumberFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const getCurrencySymbol = useCallback((code?: string): string => {
    if (!code) return '';
    const match = SUPPORTED_CURRENCIES.find(curr => curr.code === code);
    return match?.symbol || code;
  }, []);

  const formatMoney = useCallback(
    (amount: number, code?: string): string => {
      const rounded = groupedNumberFormatter.format(Number(amount) || 0);
      const symbol = getCurrencySymbol(code);
      return symbol ? `${symbol}\u00A0${rounded}` : `${rounded}`;
    },
    [getCurrencySymbol, groupedNumberFormatter]
  );

  const formatNumber = useCallback(
    (value: number): string => {
      return groupedNumberFormatter.format(Number(value) || 0);
    },
    [groupedNumberFormatter]
  );

  const showSaleProfitPlusFields = useMemo(
    () => shouldShowSaleProfitPlusFields(profitPlus2k8Config),
    [profitPlus2k8Config]
  );

  const selectedSalesPerson = useMemo(
    () => salesPeople.find(s => s.id === selectedVendorId),
    [salesPeople, selectedVendorId]
  );

  /** ERP vendedor code: only from the selected MayorX salesperson (catálogo / doc). */
  const resolvedSalesPersonCoVenForProfit = useMemo(() => {
    if (!selectedVendorId || selectedVendorId === 'admin') return '';
    const sp = selectedSalesPerson;
    if (!sp) return '';
    return String(getSalesPersonProfitCoVen(sp) ?? '').trim();
  }, [selectedVendorId, selectedSalesPerson]);

  const profitEligibilityDepsKey = useMemo(
    () =>
      [
        selectedClient?.id ?? '',
        selectedClient?.code ?? '',
        selectedVendorId,
        String(resolvedSalesPersonCoVenForProfit ?? '').trim(),
        selectedProducts
          .map(
            p =>
              `${p.id}:${String(p.code ?? '').trim()}:${String(p.selectedCoSub ?? '').trim()}`
          )
          .join('|'),
      ].join('::'),
    [
      selectedClient?.id,
      selectedClient?.code,
      selectedVendorId,
      resolvedSalesPersonCoVenForProfit,
      selectedProducts,
    ]
  );

  /** Mandatory subalmacén per line when Profit export UI is active (blocks approve / sync messaging). */
  const profitLineCoSubIssuesLocal = useMemo(
    () =>
      showSaleProfitPlusFields
        ? collectProfitLineCoSubIssues(selectedProducts)
        : [],
    [showSaleProfitPlusFields, selectedProducts]
  );

  useEffect(() => {
    if (!showSaleProfitPlusFields || !organizationId) {
      setProfitEligibilityIssues([]);
      setProfitSyncBlockers([]);
      setProfitSyncWarnings([]);
      return;
    }
    const cid = String(selectedClient?.id || '').trim();
    if (!cid || selectedProducts.length === 0) {
      setProfitEligibilityIssues([]);
      setProfitSyncBlockers([]);
      setProfitSyncWarnings([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const r = await assessOrderProfitSyncEntityEligibility(
            organizationId,
            {
              clientCode:
                String(selectedClient?.code || '').trim() || undefined,
              fallbackClientId: cid,
              productCodes: selectedProducts.map(p =>
                String(p.code || '').trim()
              ),
              fallbackSalesPersonId: selectedVendorId || undefined,
              salesPersonCoVenOverride:
                resolvedSalesPersonCoVenForProfit || undefined,
            }
          );
          if (!cancelled) {
            const blockers = r.blockers;
            const warnings = [...profitLineCoSubIssuesLocal, ...r.warnings];
            setProfitSyncBlockers(blockers);
            setProfitSyncWarnings(warnings);
            setProfitEligibilityIssues([...blockers, ...warnings]);
          }
        } catch (e) {
          console.error('Profit order eligibility check', e);
          if (!cancelled) {
            const blockers = [
              'no se pudo verificar la exportación a Profit Plus; intente de nuevo más tarde',
            ];
            setProfitSyncBlockers(blockers);
            setProfitSyncWarnings(profitLineCoSubIssuesLocal);
            setProfitEligibilityIssues([
              ...blockers,
              ...profitLineCoSubIssuesLocal,
            ]);
          }
        }
      })();
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [
    showSaleProfitPlusFields,
    organizationId,
    profitEligibilityDepsKey,
    profitLineCoSubIssuesLocal,
  ]);

  /** ERP código de moneda dólar (debe existir en referenceTables.monedas). */
  const profitUsdMonedaCode = useMemo(
    () => findProfitUsdMonedaCode(referenceTables?.monedas),
    [referenceTables?.monedas]
  );

  const applySuggestionFromOrgProfitConfig = useCallback(() => {
    if (!profitPlus2k8Config) return;
    const pp = profitPlus2k8Config;
    if (!profitUsdMonedaCode) return;
    setSaleProfitFields(prev => ({
      ...prev,
      co_sucu: (pp.orderExportAlmacen || '').trim() || prev.co_sucu,
      moneda: profitUsdMonedaCode,
      tasa: Number.isFinite(Number(exchangeRate))
        ? Number(exchangeRate)
        : prev.tasa,
      co_tran: prev.co_tran ?? '01',
      forma_pag: prev.forma_pag ?? '02',
    }));
  }, [profitPlus2k8Config, exchangeRate, profitUsdMonedaCode]);

  const enabledPaymentMethods = useMemo(
    () => availablePaymentMethods.filter(pm => pm.enabled),
    [availablePaymentMethods]
  );

  useEffect(() => {
    if (enabledPaymentMethods.length === 0) return;
    if (!enabledPaymentMethods.some(pm => pm.name === paymentMethod)) {
      setPaymentMethod(enabledPaymentMethods[0].name);
    }
  }, [enabledPaymentMethods, paymentMethod]);

  const hasAnySaleProfitField = useMemo(
    () => hasAnySaleProfitPedidoField(saleProfitFields),
    [saleProfitFields]
  );

  const missingProfitVendedorForExport = useMemo(
    () =>
      isMissingProfitVendedorForExport({
        selectedVendorId,
        selectedSalesPerson,
      }) && showSaleProfitPlusFields,
    [showSaleProfitPlusFields, selectedVendorId, selectedSalesPerson]
  );

  const profitStep1Ready = useMemo(
    () =>
      isProfitStep1Ready({
        showSaleProfitPlusFields,
        profitUsdMonedaCode,
        saleProfitFields,
        orderExportAlmacen: profitPlus2k8Config?.orderExportAlmacen,
        exchangeRate,
      }),
    [
      showSaleProfitPlusFields,
      profitUsdMonedaCode,
      saleProfitFields,
      profitPlus2k8Config?.orderExportAlmacen,
      exchangeRate,
    ]
  );

  const effectiveCoSucu = useMemo(
    () =>
      resolveEffectiveCoSucu(
        saleProfitFields,
        profitPlus2k8Config?.orderExportAlmacen
      ),
    [saleProfitFields, profitPlus2k8Config?.orderExportAlmacen]
  );

  const profitPedidoSummaryLegible = useMemo(
    () =>
      buildProfitPedidoSummaryLegible({
        referenceTables,
        saleProfitFields,
        profitUsdMonedaCode,
        effectiveCoSucu,
        resolvedSalesPersonCoVen: resolvedSalesPersonCoVenForProfit,
        selectedVendorId,
        selectedSalesPerson,
        adminDisplayEmail: getCurrentAuthUser()?.email,
        formatSalesPersonLabel: formatSalesPersonOptionLabel,
      }),
    [
      referenceTables,
      saleProfitFields.moneda,
      saleProfitFields.co_tran,
      saleProfitFields.forma_pag,
      profitUsdMonedaCode,
      effectiveCoSucu,
      resolvedSalesPersonCoVenForProfit,
      selectedVendorId,
      selectedSalesPerson,
    ]
  );

  const coSubForSelectedAlmacen = useMemo(
    () =>
      resolveCoSubForAlmacen(
        effectiveCoSucu,
        referenceTables?.subAlmacenes,
        profitPlus2k8Config?.defaultProductStockSubAlmacen
      ),
    [
      effectiveCoSucu,
      referenceTables?.subAlmacenes,
      profitPlus2k8Config?.defaultProductStockSubAlmacen,
    ]
  );

  const subAlmacenOptions = useMemo(
    () =>
      buildSubAlmacenOptionsFromReferenceTables(referenceTables?.subAlmacenes),
    [referenceTables?.subAlmacenes]
  );

  const pickRecommendedCoSubForLine = useCallback(
    (p: Product | SelectedProduct, targetQty: number, currentCoSub?: string) =>
      pickRecommendedCoSubForLineFn({
        product: p,
        targetQty,
        currentCoSub,
        subAlmacenOptions,
        coSubForSelectedAlmacen,
      }),
    [subAlmacenOptions, coSubForSelectedAlmacen]
  );

  /** Catalog `availability` (MayorX), not per–sub-almacén warehouse stock from Profit. */
  const resolveCatalogAvailability = useCallback(
    (p: Product | SelectedProduct) => {
      const availability = Number(p.availability);
      return Number.isFinite(availability) ? availability : 0;
    },
    []
  );

  const preferredSaleWarehouseId =
    warehouseId || defaultWarehouseId || '';

  /** When editing a pending multi-warehouse sale, free sellable under-counts this order's holds. */
  const creditReservedForEditLine = useCallback(
    (productId: string, lineWarehouseId: string | undefined) => {
      const holdOrder = editHoldOrderRef.current || order;
      if (
        !isEditing ||
        !multiWarehouseEnabled ||
        !holdOrder ||
        holdOrder.approvalState !== 'pending'
      ) {
        return 0;
      }
      return reservedQtyHeldByOrderLines(
        holdOrder.products,
        productId,
        lineWarehouseId,
        holdOrder.warehouseId
      );
    },
    [isEditing, multiWarehouseEnabled, order]
  );

  const resolveLineSellable = useCallback(
    (p: SelectedProduct, lines: SelectedProduct[] = selectedProducts) => {
      if (!multiWarehouseEnabled) {
        return resolveCatalogAvailability(p);
      }
      const catalogId = catalogProductIdFromLine(p);
      const lineWarehouseId =
        p.warehouseId || warehouseId || defaultWarehouseId || '';
      const balances = balancesByProductId[catalogId];
      if (balances !== undefined) {
        const credit = creditReservedForEditLine(catalogId, lineWarehouseId);
        const siblings = siblingDemandAtWarehouse(
          lines,
          p.id,
          catalogId,
          lineWarehouseId,
          warehouseId,
          defaultWarehouseId
        );
        return lineSellableWithSharedPool(
          balances,
          lineWarehouseId,
          credit,
          siblings
        );
      }
      return resolveCatalogAvailability(p);
    },
    [
      multiWarehouseEnabled,
      warehouseId,
      defaultWarehouseId,
      balancesByProductId,
      resolveCatalogAvailability,
      creditReservedForEditLine,
      selectedProducts,
    ]
  );

  const hydrateBalancesForProducts = useCallback(
    async (productIds: string[]) => {
      if (!multiWarehouseEnabled || !organizationId) return;
      const uniqueIds = Array.from(new Set(productIds.filter(Boolean)));
      if (uniqueIds.length === 0) return;

      setBalancesLoadingProductIds(prev => {
        const next = { ...prev };
        for (const productId of uniqueIds) {
          next[productId] = true;
        }
        return next;
      });

      try {
        const entries = await listStockBalancesForProducts(
          organizationId,
          uniqueIds
        );
        const balancesById = new Map(
          entries.map(entry => [entry.productId, entry.balances])
        );
        setBalancesByProductId(prev => {
          const next = { ...prev };
          for (const productId of uniqueIds) {
            next[productId] = balancesById.get(productId) ?? [];
          }
          return next;
        });
      } catch {
        // Keep previously loaded balances when refresh fails.
      } finally {
        setBalancesLoadingProductIds(prev => {
          const next = { ...prev };
          for (const productId of uniqueIds) {
            delete next[productId];
          }
          return next;
        });
      }
    },
    [multiWarehouseEnabled, organizationId]
  );

  const resolveCatalogPickerSellable = useCallback(
    (p: Product) => {
      if (!multiWarehouseEnabled) {
        return resolveCatalogAvailability(p);
      }
      const balances = balancesByProductId[p.id];
      if (balances !== undefined) {
        return sellableAtWarehouse(balances, preferredSaleWarehouseId);
      }
      return resolveCatalogAvailability(p);
    },
    [
      multiWarehouseEnabled,
      balancesByProductId,
      preferredSaleWarehouseId,
      resolveCatalogAvailability,
    ]
  );

  const resolveLineAvailabilityCap = useCallback(
    (p: SelectedProduct, lines?: SelectedProduct[]) => {
      if (configurationFactors.allowNegativeInventory) {
        return undefined;
      }
      const catalogId = catalogProductIdFromLine(p);
      if (
        multiWarehouseEnabled &&
        !isProductStockBalancesLoaded(balancesByProductId[catalogId])
      ) {
        return undefined;
      }
      const availability = resolveLineSellable(p, lines);
      // Cap at loaded availability including 0 so empty stock cannot be typed past.
      if (!Number.isFinite(availability)) return undefined;
      return Math.max(0, availability);
    },
    [
      multiWarehouseEnabled,
      balancesByProductId,
      resolveLineSellable,
      configurationFactors.allowNegativeInventory,
    ]
  );

  const isProductBalancesLoading = useCallback(
    (productId: string) => Boolean(balancesLoadingProductIds[productId]),
    [balancesLoadingProductIds]
  );

  const resolveCreatorSalesPersonString = useCallback(
    (): string =>
      buildCreatorSalesPersonString({
        selectedVendorId,
        salesPeople,
        currentUserEmail: getCurrentAuthUser()?.email,
      }),
    [selectedVendorId, salesPeople]
  );

  /** Apply first-option Profit defaults once per modal open after reference data loads. */
  const profitDefaultsAppliedRef = useRef(false);
  /** After `getOrderServer`, apply authoritative `profitPlus2k8.fields` (co_sucu, etc.) once. */
  const profitPedidoHydratedFromApiRef = useRef(false);
  /** Map `order.creatorSalesPerson` (email/code/name) → `selectedVendorId` once per open. */
  const creatorSalesPersonResolvedRef = useRef(false);
  /**
   * Authoritative creatorSalesPerson from getOrderServer (list snapshot may differ).
   */
  const hydratedCreatorSalesPersonRef = useRef<string | null>(null);
  /**
   * Edit/approve must wait for full sale hydrate so line warehouses / credit are not
   * invented from the shallow list snapshot or header defaults.
   */
  const [orderServerHydrated, setOrderServerHydrated] = useState(!isEditing);
  /** Authoritative pending holds for Disp. credit (list snapshot until hydrate). */
  const editHoldOrderRef = useRef<Order | null>(order ?? null);

  /** Keep `saleProfitFields.co_ven` aligned with the single vendedor selection (for persisted pedido fields). */
  useEffect(() => {
    if (!showSaleProfitPlusFields) return;
    const co =
      selectedVendorId === 'admin' || !selectedVendorId
        ? undefined
        : resolvedSalesPersonCoVenForProfit || undefined;
    setSaleProfitFields(prev => {
      const want = String(co ?? '').trim();
      const cur = String(prev.co_ven ?? '').trim();
      if (cur === want) return prev;
      return { ...prev, co_ven: co };
    });
  }, [
    showSaleProfitPlusFields,
    selectedVendorId,
    resolvedSalesPersonCoVenForProfit,
  ]);
  const getClientDefaultAddress = useCallback(
    (client: Client | null): string => {
      if (!client) return '';
      const candidates = [
        client.shippingAddress,
        client.alternativeShippingAddress,
        client.fiscalAddress,
        client.address,
      ]
        .map(value => (value || '').trim())
        .filter(Boolean);
      return candidates[0] || '';
    },
    []
  );

  const dateToInputValue = useCallback((date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const resolveDueDateFromTerms = useCallback(
    (rawTerms: number | string): string => {
      const parsed = Number(rawTerms);
      const safeTerms =
        Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0;
      const base =
        emissionDate && /^\d{4}-\d{2}-\d{2}$/.test(emissionDate)
          ? new Date(`${emissionDate}T12:00:00`)
          : new Date();
      const due = new Date(base);
      due.setDate(due.getDate() + safeTerms);
      return dateToInputValue(due);
    },
    [dateToInputValue, emissionDate]
  );

  const getClientCreditTermsDays = useCallback(
    (client: Client | null): number | null => {
      if (!client) return null;
      const parsed = Number(client.creditTerms);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return Math.round(parsed);
    },
    []
  );

  const applyClientCreditDefaults = useCallback(
    (client: Client | null) => {
      if (!client) return;
      const terms = getClientCreditTermsDays(client);
      if (terms === null) {
        setCreditEnabled(false);
        setCreditTermsDays('');
        setCreditDueDate('');
        return;
      }
      setCreditEnabled(true);
      setCreditTermsDays(String(terms));
      setCreditDueDate(resolveDueDateFromTerms(terms));
    },
    [getClientCreditTermsDays, resolveDueDateFromTerms]
  );

  const getProductTaxInfo = useCallback(
    (product: SelectedProduct) =>
      resolveProductTaxInfo(product, configurationFactors),
    [configurationFactors]
  );

  const getOrderLineTaxForPersistence = useCallback(
    (product: SelectedProduct) =>
      resolveOrderLineTaxForPersistence(
        product,
        configurationFactors,
        applyIva
      ),
    [applyIva, configurationFactors]
  );

  const totals = useMemo(
    () =>
      computeOrderTotals(selectedProducts, {
        applyIva,
        taxConfig: configurationFactors,
        orderDiscountPercent,
      }),
    [selectedProducts, applyIva, configurationFactors, orderDiscountPercent]
  );

  // Load products (initial search: higher limit so one word shows all matches)
  const fetchProducts = useCallback(async () => {
    setProductPending(true);
    try {
      const result = await loadProducts(
        organizationId,
        productSearch.trim() || undefined,
        undefined,
        100
      );
      setProducts(result.products);
      setProductLastDoc(result.lastDoc ?? null);
      setProductHasMore(result.hasMore);
      if (multiWarehouseEnabled) {
        void hydrateBalancesForProducts(
          result.products.map(product => product.id).filter(Boolean)
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      setProductPending(false);
    }
  }, [organizationId, productSearch, multiWarehouseEnabled, hydrateBalancesForProducts]);

  const loadMoreProducts = useCallback(async () => {
    if (!productHasMore || productLastDoc == null) return;
    setProductLoadMorePending(true);
    try {
      const result = await loadProducts(
        organizationId,
        productSearch.trim() || undefined,
        productLastDoc,
        100
      );
      setProducts(prev => prev.concat(result.products));
      setProductLastDoc(result.lastDoc ?? null);
      setProductHasMore(result.hasMore);
      if (multiWarehouseEnabled) {
        void hydrateBalancesForProducts(
          result.products.map(product => product.id).filter(Boolean)
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      setProductLoadMorePending(false);
    }
  }, [
    organizationId,
    productSearch,
    productLastDoc,
    productHasMore,
    multiWarehouseEnabled,
    hydrateBalancesForProducts,
  ]);

  // Load clients on demand
  const fetchClients = useCallback(async () => {
    setClientPending(true);
    try {
      const result = await loadClients(organizationId, clientSearch, 10);
      setClients(result.clients);
    } catch (e) {
      console.log(e);
    } finally {
      setClientPending(false);
    }
  }, [organizationId, clientSearch]);

  // Load sales people
  const fetchVendors = useCallback(async () => {
    setVendorPending(true);
    try {
      const vs = await searchSalesPeople(organizationId);

      // Add current admin as a vendor option
      const currentUser = getCurrentAuthUser();
      if (currentUser) {
        const adminSalesPerson: SalesPerson = {
          id: 'admin',
          email: currentUser.email || undefined,
          name: currentUser.displayName || 'Administrador',
          created: new Date(),
        };

        // Add admin as first option
        setSalesPeople([adminSalesPerson, ...vs]);
      } else {
        setSalesPeople(vs);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setVendorPending(false);
    }
  }, [organizationId]);

  // Load configuration to get available payment methods and IVA factors
  const fetchConfiguration = useCallback(async () => {
    setProfitReferenceDataReady(false);
    try {
      const config = await loadConfigurationFactors(organizationId);
      const paymentMethods = config.paymentMethodsAvailable || [];

      setAvailablePaymentMethods(paymentMethods);

      // Set default payment method to first enabled method only on create
      // (on edit we keep the existing payment method from the order).
      const firstEnabled = paymentMethods.find(pm => pm.enabled);
      if (!isEditing && firstEnabled) {
        setPaymentMethod(firstEnabled.name);
      }

      // Set currency and referenceCurrency
      setCurrency(config.currency || 'VES');
      setReferenceCurrency(config.referenceCurrency || 'USD');

      // Set default paymentCurrency to currency only on create
      // (on edit we keep the existing paymentCurrency from the order).
      if (!isEditing || !order?.paymentCurrency) {
        setPaymentCurrency(config.currency || 'VES');
      }

      // Set IVA factors and approval settings
      setConfigurationFactors({
        iva: config.iva,
        orderApprovalRequired: config.orderApprovalRequired || false,
        allowNegativeInventory: Boolean(config.allowNegativeInventory),
        blockSalesWhenInsufficientStock: Boolean(
          config.blockSalesWhenInsufficientStock
        ),
        taxes: config.taxes || [],
        primaryTaxId: config.primaryTaxId || '',
      });
      setMultiWarehouseEnabled(
        isMultiWarehouseMode(resolveInventoryMode(config.inventoryMode))
      );
      setDefaultWarehouseId(config.defaultWarehouseId?.trim() || '');

      const pp = await loadProfitPlus2k8IntegrationConfig(organizationId);
      setProfitPlus2k8Config(pp);
      if (pp?.enabled) {
        const rt = await loadProfitPlus2k8ReferenceTables(organizationId, [
          'almacenes',
          'subAlmacenes',
          'vendedoresProfit',
          'monedas',
          'condiciones',
          'transportistas',
        ]);
        setReferenceTables(rt);
      } else {
        setReferenceTables(null);
      }
    } catch (e) {
      console.error('Failed to load configuration:', e);
      // Show error to user and prevent order creation
      addToast(
        `Error: ${
          e instanceof Error ? e.message : 'Failed to load configuration'
        }`
      );
      onClose(); // Close the form since we can't proceed without configuration
    } finally {
      setProfitReferenceDataReady(true);
    }
  }, [organizationId, addToast, onClose, isEditing, order?.paymentCurrency]);

  useEffect(() => {
    if (!show || !multiWarehouseEnabled) {
      setWarehouses([]);
      return;
    }
    let cancelled = false;
    void listAllActiveWarehouses(organizationId)
      .then(items => {
        if (!cancelled) setWarehouses(items);
      })
      .catch(() => {
        if (!cancelled) setWarehouses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [show, multiWarehouseEnabled, organizationId]);

  useEffect(() => {
    if (!show || !multiWarehouseEnabled || warehouseId) return;
    // Never invent a header warehouse while editing an existing sale.
    if (isEditing) return;
    const defaultWh =
      warehouses.find(w => w.id === defaultWarehouseId) ||
      warehouses.find(w => w.isDefault) ||
      warehouses[0];
    if (defaultWh?.id) setWarehouseId(defaultWh.id);
  }, [
    show,
    multiWarehouseEnabled,
    warehouses,
    defaultWarehouseId,
    warehouseId,
    isEditing,
  ]);

  const selectedProductsCatalogKey = useMemo(
    () =>
      Array.from(
        new Set(
          selectedProducts.map(p => catalogProductIdFromLine(p)).filter(Boolean)
        )
      )
        .sort()
        .join('|'),
    [selectedProducts]
  );

  useEffect(() => {
    if (!show || !multiWarehouseEnabled || !organizationId) return;
    if (!selectedProductsCatalogKey) return;
    const productIds = selectedProductsCatalogKey.split('|').filter(Boolean);
    void hydrateBalancesForProducts(productIds);
  }, [
    show,
    multiWarehouseEnabled,
    organizationId,
    selectedProductsCatalogKey,
    hydrateBalancesForProducts,
  ]);

  useEffect(() => {
    if (!show || !multiWarehouseEnabled || !organizationId || isEditing) return;
    const productIds = Array.from(
      new Set(products.map(product => product.id).filter(Boolean))
    );
    if (productIds.length === 0) return;
    void hydrateBalancesForProducts(productIds);
  }, [
    show,
    multiWarehouseEnabled,
    organizationId,
    products,
    hydrateBalancesForProducts,
    isEditing,
  ]);

  // Load configuration factors to get exchange rate
  const fetchConfigurationFactors = useCallback(async () => {
    try {
      const config = await loadConfigurationFactors(organizationId);
      setConfigurationFactors({
        iva: config.iva,
        orderApprovalRequired: config.orderApprovalRequired ?? false,
        allowNegativeInventory: Boolean(config.allowNegativeInventory),
        blockSalesWhenInsufficientStock: Boolean(
          config.blockSalesWhenInsufficientStock
        ),
        taxes: config.taxes || [],
        primaryTaxId: config.primaryTaxId || '',
      });
      const hasPrefilledExchangeRate =
        isEditing && order && typeof order.exchangeRate === 'number';
      if (config.exchangeRate) {
        setDefaultExchangeRate(config.exchangeRate);
        if (!hasPrefilledExchangeRate) {
          setExchangeRate(config.exchangeRate);
        }
      }
      setOrgUsesAutomaticExchangeRate(Boolean(config.useAutomaticExchangeRate));
      setDefaultExchangeSourceId(
        config.useAutomaticExchangeRate ? config.exchangeSourceId || '' : ''
      );
      // Set currency and referenceCurrency
      setCurrency(config.currency || 'VES');
      setReferenceCurrency(config.referenceCurrency || 'USD');
      // Set default paymentCurrency to currency only on create
      if (!isEditing || !order?.paymentCurrency) {
        setPaymentCurrency(config.currency || 'VES');
      }
    } catch (e) {
      console.error('Failed to load configuration factors:', e);
    }
  }, [organizationId, isEditing, order?.paymentCurrency, order?.exchangeRate]);

  // Load exchange sources for referenceCurrency -> currency (for per-order overrides)
  useEffect(() => {
    const loadSources = async () => {
      if (!referenceCurrency || !currency) {
        setExchangeSources([]);
        return;
      }
      try {
        const sources = await listExchangeSourcesForPair({
          fromCurrency: referenceCurrency,
          toCurrency: currency,
        });
        const mapped: ExchangeSourceOption[] = sources.map(s => ({
          id: s.id,
          name: s.name,
          rate: s.rate,
          sourceUrl: s.sourceUrl,
          lastFetchedAt: s.lastFetchedAt,
        }));
        setExchangeSources(mapped);
      } catch (e) {
        console.warn('Failed to load exchange sources', e);
        setExchangeSources([]);
      }
    };
    loadSources();
  }, [currency, referenceCurrency]);

  useEffect(() => {
    if (show) {
      setQuantityDraftByProductId({});
      setDiscountDraftByProductId({});
      setBalancesByProductId({});
      setBalancesLoadingProductIds({});
      creatorSalesPersonResolvedRef.current = false;
      profitPedidoHydratedFromApiRef.current = false;
      setOrderServerHydrated(!isEditing);
      editHoldOrderRef.current = isEditing && order ? order : null;
      setHydratedApprovalState(isEditing ? order?.approvalState : undefined);

      // In edit mode we take the user straight to the overview section (step 3),
      // so they can see the selected client and products immediately.
      setStep(isEditing ? 3 : 1);

      if (isEditing && order) {
        const prefillSelectedProducts = mapOrderLinesToSelectedProducts(order);

        const subtotal = Number(order.subtotal) || 0;
        const total = Number(order.total) || 0;
        const storedIva = Number(order.ivaAmount) || 0;
        const inferredIva = total - subtotal;
        const ivaAmountToUse =
          Math.abs(subtotal + storedIva - total) > 0.01
            ? inferredIva
            : storedIva;

        setSelectedProducts(prefillSelectedProducts);

        // Minimal client prefill so the wizard can proceed.
        setSelectedClient({
          id: order.clientId,
          code: '',
          rif: '',
          businessName: order.clientName,
          legalName: order.clientName,
        } as any);

        setDeliveryAddress(order.deliveryAddress || '');
        setSelectedVendorId(order.creatorSalesPerson || '');
        setPaymentMethod(
          order.paymentMethod || PAYMENT_METHOD_LABELS[PAYMENT_METHODS.CASH]
        );
        setWarehouseId(order.warehouseId || defaultWarehouseId);
        setPaymentCurrency(order.paymentCurrency || '');
        const creditUi = creditUiStateFromOrder(order, dateToInputValue);
        setCreditEnabled(creditUi.creditEnabled);
        setCreditTermsDays(creditUi.creditTermsDays);
        setCreditDueDate(creditUi.creditDueDate);
        setComment(order.comment || '');
        setOrderDiscountPercent(
          normalizeDiscountPercent(order.orderDiscountPercent)
        );
        setExchangeRate(
          typeof order.exchangeRate === 'number'
            ? order.exchangeRate
            : defaultExchangeRate
        );
        setExchangeRateMode(
          typeof order.exchangeRate === 'number' ? 'saleDefault' : 'orgDefault'
        );
        setApplyIva(Math.abs(ivaAmountToUse) > 0.01);
        setExchangeSourceOverrideId('');

        const pedido = orderDocumentProfitPlus2k8FromOrder(order);
        const pf = pedido?.fields;
        if (pf && typeof pf === 'object') {
          setSaleProfitFields({ ...pf });
        } else {
          setSaleProfitFields({});
        }
        /**
         * Always allow `useLayoutEffect` to fill empty sucursal / transporte / condición once
         * reference tables load. A truthy but empty `fields: {}` used to set
         * `profitDefaultsAppliedRef` and block that effect entirely, leaving co_sucu unset.
         */
        profitDefaultsAppliedRef.current = false;
        // In edit mode: don't auto-load the products catalog list.
        setShouldShowProductCatalog(false);

        // Fetch full client info to show complete "Cliente Seleccionado".
        // Keep Firestore reads inside the API folder.
        (async () => {
          try {
            if (!order.clientId) return;
            const fullClient = await getClientById(
              organizationId,
              order.clientId
            );
            if (!fullClient) return;

            setSelectedClient(fullClient);

            // Never invent client credit defaults when editing an existing sale.
            // Keep order.deliveryAddress as-is (including empty); do not fill from client.
          } catch (e) {
            console.warn('Failed to load client for sale edit:', e);
          }
        })();
      } else {
        setSelectedProducts([]);
        setSelectedClient(null);
        setDeliveryAddress('');
        setSelectedVendorId('');
        setPaymentMethod(PAYMENT_METHOD_LABELS[PAYMENT_METHODS.CASH]);
        setPaymentCurrency('');
        setCreditEnabled(false);
        setCreditTermsDays('');
        setCreditDueDate('');
        setEmissionDate(dateToInputValue(new Date()));
        setComment('');
        setOrderDiscountPercent(0);
        setWarehouseId('');
        setApplyIva(true);
        setExchangeRateMode('orgDefault');

        setSaleProfitFields({});
        profitDefaultsAppliedRef.current = false;

        // In create mode: load products catalog immediately.
        setShouldShowProductCatalog(true);
      }

      // Call the latest functions without tying the effect to their identities
      if (!isEditing) {
        fetchProducts();
      } else {
        // Make sure the previous catalog isn't showing in edit mode.
        setProducts([]);
        setProductHasMore(false);
        setProductPending(false);
        setProductLoadMorePending(false);
      }
      fetchClients();
      fetchVendors();
      fetchConfiguration();
      fetchConfigurationFactors();
    } else {
      profitDefaultsAppliedRef.current = false;
    }
    // Intentionally only depend on `show` so searching/typing won't reset the wizard
  }, [show]);

  /**
   * List views can pass a shallow order snapshot. Load the full sale and replace
   * form commercial fields from the server before save/approve is allowed.
   */
  useEffect(() => {
    if (!show || !isEditing || !order?.id) {
      if (!isEditing) {
        setOrderServerHydrated(true);
      }
      return;
    }
    let cancelled = false;
    setOrderServerHydrated(false);
    (async () => {
      try {
        const fresh = await getOrderServer(organizationId, order.id);
        if (cancelled) return;
        if (!fresh) {
          console.warn('Could not hydrate sale: empty response');
          addToast('No se pudo cargar la venta completa. Intente de nuevo.');
          return;
        }

        editHoldOrderRef.current = fresh;
        setHydratedApprovalState(fresh.approvalState);
        hydratedCreatorSalesPersonRef.current = fresh.creatorSalesPerson || '';
        creatorSalesPersonResolvedRef.current = false;
        setSelectedProducts(mapOrderLinesToSelectedProducts(fresh));
        setDeliveryAddress(fresh.deliveryAddress || '');
        setSelectedVendorId(fresh.creatorSalesPerson || '');
        setPaymentMethod(
          fresh.paymentMethod || PAYMENT_METHOD_LABELS[PAYMENT_METHODS.CASH]
        );
        setWarehouseId(fresh.warehouseId || '');
        setPaymentCurrency(fresh.paymentCurrency || '');
        const creditUi = creditUiStateFromOrder(fresh, dateToInputValue);
        setCreditEnabled(creditUi.creditEnabled);
        setCreditTermsDays(creditUi.creditTermsDays);
        setCreditDueDate(creditUi.creditDueDate);
        setComment(fresh.comment || '');
        setOrderDiscountPercent(
          normalizeDiscountPercent(fresh.orderDiscountPercent)
        );
        if (typeof fresh.exchangeRate === 'number') {
          setExchangeRate(fresh.exchangeRate);
          setExchangeRateMode('saleDefault');
        }
        const subtotal = Number(fresh.subtotal) || 0;
        const total = Number(fresh.total) || 0;
        const storedIva = Number(fresh.ivaAmount) || 0;
        const inferredIva = total - subtotal;
        const ivaAmountToUse =
          Math.abs(subtotal + storedIva - total) > 0.01
            ? inferredIva
            : storedIva;
        setApplyIva(Math.abs(ivaAmountToUse) > 0.01);

        if (!profitPedidoHydratedFromApiRef.current) {
          profitPedidoHydratedFromApiRef.current = true;
          const sf = orderDocumentProfitPlus2k8FromOrder(fresh)?.fields;
          if (sf && typeof sf === 'object' && !Array.isArray(sf)) {
            setSaleProfitFields(prev =>
              applyAuthoritativePedidoFieldsFromServer(
                prev,
                sf as Record<string, unknown>
              )
            );
          }
        }

        if (!cancelled) {
          setOrderServerHydrated(true);
        }
      } catch (e) {
        console.warn('Could not hydrate sale lines from server:', e);
        addToast('No se pudo cargar la venta completa. Intente de nuevo.');
        // Fail closed: keep Save/Approve blocked until a successful hydrate.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, isEditing, order?.id, organizationId, dateToInputValue, addToast]);

  /**
   * Sale lines are built from the order snapshot (no `profitPlus2k8.warehouseStock`). Merge
   * per–sub-almacén stock from each product doc so sub-almacén auto-pick and Profit inventory
   * warnings stay accurate when editing.
   */
  const editLineWarehouseFetchKey = useMemo(
    () =>
      show &&
      isEditing &&
      order?.id &&
      showSaleProfitPlusFields &&
      profitReferenceDataReady
        ? `${order.id}:${(order.products || [])
            .map(p => p.productId)
            .filter(Boolean)
            .join(',')}`
        : '',
    [
      show,
      isEditing,
      order?.id,
      order?.products,
      showSaleProfitPlusFields,
      profitReferenceDataReady,
    ]
  );

  useEffect(() => {
    if (!editLineWarehouseFetchKey || !organizationId) return;
    const ids = (order?.products || []).map(p => p.productId).filter(Boolean);
    if (!ids.length) return;
    let cancelled = false;
    void (async () => {
      try {
        const fetched = await Promise.all(
          ids.map(id => fetchProductById(organizationId, id))
        );
        if (cancelled) return;
        setSelectedProducts(prev => {
          if (prev.length === 0) return prev;
          const byId = new Map(
            fetched
              .filter((row): row is Product => Boolean(row))
              .map(row => [row.id, row])
          );
          let changed = false;
          const next = prev.map(sp => {
            const catalogId = catalogProductIdFromLine(sp);
            const fp = byId.get(catalogId) || byId.get(sp.id);
            const wh = fp?.profitPlus2k8?.warehouseStock;
            if (!fp?.profitPlus2k8 || !wh || typeof wh !== 'object') return sp;
            const existing = sp.profitPlus2k8?.warehouseStock;
            if (
              existing &&
              typeof existing === 'object' &&
              Object.keys(existing).length > 0
            ) {
              return sp;
            }
            changed = true;
            return {
              ...sp,
              profitPlus2k8: {
                ...(sp.profitPlus2k8 ?? {}),
                ...fp.profitPlus2k8,
                warehouseStock: wh,
              },
            };
          });
          return changed ? next : prev;
        });
      } catch (e) {
        console.warn(
          'Failed to merge product warehouse stock for sale lines',
          e
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    editLineWarehouseFetchKey,
    organizationId,
    order?.products,
    selectedProducts.length,
  ]);

  useEffect(() => {
    if (!show || !isEditing) return;
    creatorSalesPersonResolvedRef.current = false;
  }, [show, isEditing, order?.id]);

  useEffect(() => {
    if (!show || !isEditing) return;
    const creator =
      hydratedCreatorSalesPersonRef.current || order?.creatorSalesPerson || '';
    if (!creator.trim()) return;
    if (vendorPending) return;
    if (salesPeople.length === 0) return;
    if (creatorSalesPersonResolvedRef.current) return;

    const resolved = resolveStoredCreatorSalesPersonToVendorId(
      creator,
      salesPeople,
      { currentUserEmail: getCurrentAuthUser()?.email }
    );
    creatorSalesPersonResolvedRef.current = true;
    setSelectedVendorId(prev =>
      resolved && resolved !== prev ? resolved : prev
    );
  }, [
    show,
    isEditing,
    order?.id,
    order?.creatorSalesPerson,
    salesPeople,
    vendorPending,
    orderServerHydrated,
  ]);

  useEffect(() => {
    if (!showSaleProfitPlusFields || !profitUsdMonedaCode) return;
    setSaleProfitFields(prev => {
      if (String(prev.moneda ?? '').trim() === profitUsdMonedaCode) return prev;
      return { ...prev, moneda: profitUsdMonedaCode };
    });
  }, [showSaleProfitPlusFields, profitUsdMonedaCode]);

  /** First option in each Profit dropdown when still empty (order matches each Form.Select list). */
  useLayoutEffect(() => {
    if (!show) return;
    if (
      !profitReferenceDataReady ||
      !showSaleProfitPlusFields ||
      !referenceTables
    )
      return;
    if (profitDefaultsAppliedRef.current) return;
    profitDefaultsAppliedRef.current = true;
    setSaleProfitFields(prev => {
      const next = { ...prev };
      let changed = false;
      const almas = referenceTables.almacenes;
      const savedAlma = (profitPlus2k8Config?.orderExportAlmacen ?? '').trim();
      if (!String(next.co_sucu ?? '').trim()) {
        let coSucu = '';
        if (almas?.length) {
          const hasSaved =
            Boolean(savedAlma) &&
            almas.some(a => String(a.co_alma ?? '').trim() === savedAlma);
          coSucu = hasSaved ? savedAlma : String(almas[0].co_alma ?? '').trim();
        } else if (savedAlma) {
          coSucu = savedAlma;
        }
        if (coSucu) {
          next.co_sucu = coSucu;
          changed = true;
        }
      }
      if (profitUsdMonedaCode) {
        if (String(next.moneda ?? '').trim() !== profitUsdMonedaCode) {
          next.moneda = profitUsdMonedaCode;
          changed = true;
        }
      }
      const trans = referenceTables.transportistas;
      if (trans?.length && !String(next.co_tran ?? '').trim()) {
        const sorted = [...trans].sort((a, b) =>
          String(a.des_tran ?? '').localeCompare(String(b.des_tran ?? ''), 'es')
        );
        const c = sorted.map(t => String(t.co_tran ?? '').trim()).find(Boolean);
        if (c) {
          next.co_tran = c;
          changed = true;
        }
      }
      const conds = referenceTables.condiciones;
      if (conds?.length && !String(next.forma_pag ?? '').trim()) {
        const sorted = [...conds].sort(
          (a, b) =>
            (Number(a.dias_cred) || 0) - (Number(b.dias_cred) || 0) ||
            String(a.cond_des ?? '').localeCompare(
              String(b.cond_des ?? ''),
              'es'
            )
        );
        const c = sorted.map(x => String(x.co_cond ?? '').trim()).find(Boolean);
        if (c) {
          next.forma_pag = c;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    show,
    profitReferenceDataReady,
    showSaleProfitPlusFields,
    referenceTables,
    profitPlus2k8Config?.orderExportAlmacen,
    profitUsdMonedaCode,
  ]);

  /** Profit usa siempre la tasa activa de la venta (no configura tasa aparte). */
  useEffect(() => {
    if (!showSaleProfitPlusFields) return;
    const rate = Number(exchangeRate);
    if (!Number.isFinite(rate) || rate <= 0) return;
    setSaleProfitFields(prev => ({
      ...prev,
      tasa: rate,
    }));
  }, [showSaleProfitPlusFields, exchangeRate]);

  useEffect(() => {
    if (!selectedClient) return;

    // When editing the same client, keep the sale's delivery address (even if empty)
    // until the user explicitly picks a different client.
    if (isEditing && order && selectedClient.id === order.clientId) {
      return;
    }

    setDeliveryAddress(getClientDefaultAddress(selectedClient));
  }, [selectedClient, getClientDefaultAddress, isEditing, order]);

  useEffect(() => {
    setShowClientMoreInfo(false);
  }, [selectedClient?.id]);

  useEffect(() => {
    // Create mode only: inputs already set due date onChange. In edit mode this
    // would overwrite the saved order.dueDate with "today + terms" on open.
    if (isEditing) return;
    if (!creditEnabled) return;
    const termsText = creditTermsDays.trim();
    if (!termsText) {
      setCreditDueDate('');
      return;
    }
    const parsed = Number(termsText);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    if (parsed <= 0) {
      setCreditEnabled(false);
      setCreditTermsDays('');
      setCreditDueDate('');
      return;
    }
    setCreditDueDate(resolveDueDateFromTerms(parsed));
  }, [isEditing, creditEnabled, creditTermsDays, resolveDueDateFromTerms]);

  useEffect(() => {
    // Keep org-default mode synced with the currently effective org rate.
    if (exchangeRateMode !== 'orgDefault') return;
    setExchangeRate(orgEffectiveExchangeRate);
  }, [exchangeRateMode, orgEffectiveExchangeRate]);

  useEffect(() => {
    if (!showSaleProfitPlusFields || subAlmacenOptions.length === 0) return;
    // Never auto-reassign sub-almacén when editing an existing sale: ref-table / sucursal
    // updates must not replace persisted line codes (also avoids effect-order races with prefill).
    if (isEditing) return;
    setSelectedProducts(prev => {
      let changed = false;
      const next = prev.map(p => {
        const qty = Number(p.selectedQuantity) || 0;
        const recommended = pickRecommendedCoSubForLine(
          p,
          qty,
          p.selectedCoSub
        );
        if (recommended === p.selectedCoSub) return p;
        changed = true;
        return { ...p, selectedCoSub: recommended };
      });
      return changed ? next : prev;
    });
  }, [
    showSaleProfitPlusFields,
    subAlmacenOptions,
    pickRecommendedCoSubForLine,
    effectiveCoSucu,
    isEditing,
  ]);

  const addProduct = (p: Product) => {
    const catalogId = String(p.id || '').trim();
    if (
      selectedProducts.find(
        sp => catalogProductIdFromLine(sp) === catalogId || sp.id === p.id
      )
    ) {
      return;
    }
    const selectedCoSub = showSaleProfitPlusFields
      ? pickRecommendedCoSubForLine(p, 1, undefined)
      : undefined;
    const balances = balancesByProductId[p.id];
    const lineWarehouseId =
      multiWarehouseEnabled && preferredSaleWarehouseId
        ? resolveWarehouseWithSufficientSellable(
            balances,
            1,
            preferredSaleWarehouseId,
            warehouses
          )
        : preferredSaleWarehouseId || undefined;
    setSelectedProducts(prev =>
      prev.concat({
        ...p,
        productId: p.id,
        selectedQuantity: 1,
        discount: 0,
        selectedCoSub,
        warehouseId: lineWarehouseId,
      })
    );
  };

  const removeProduct = (id: string) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== id));
    setQuantityDraftByProductId(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDiscountDraftByProductId(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const commitQuantity = useCallback(
    (id: string, raw: number) => {
      if (!Number.isFinite(raw) || raw <= 0) return;
      const lockWarehouseOnQtyChange =
        warehouseLocked ||
        (isEditing &&
          multiWarehouseEnabled &&
          effectiveApprovalState === 'pending');
      setSelectedProducts(prev =>
        prev.map(p => {
          if (p.id !== id) return p;
          const catalogId = catalogProductIdFromLine(p);
          const balances = balancesByProductId[catalogId];
          const preferredWarehouse =
            p.warehouseId || preferredSaleWarehouseId || '';
          const creditAtPreferred = creditReservedForEditLine(
            catalogId,
            preferredWarehouse
          );
          const nextWarehouse =
            multiWarehouseEnabled &&
            preferredWarehouse &&
            !lockWarehouseOnQtyChange
              ? resolveWarehouseWithSufficientSellable(
                  balances,
                  raw,
                  preferredWarehouse,
                  warehouses,
                  creditAtPreferred
                )
              : p.warehouseId;
          const lineForCap: SelectedProduct = {
            ...p,
            warehouseId: nextWarehouse,
          };
          const recommendedCoSub = showSaleProfitPlusFields
            ? pickRecommendedCoSubForLine(lineForCap, raw, p.selectedCoSub)
            : p.selectedCoSub;
          const tentative = prev.map(row =>
            row.id === id
              ? { ...row, warehouseId: nextWarehouse, selectedQuantity: raw }
              : row
          );
          const maxQty = resolveLineAvailabilityCap(lineForCap, tentative);
          let nextQty = raw;
          if (maxQty !== undefined) {
            // At 0 availability, allow decrease but not increase.
            nextQty =
              maxQty <= 0
                ? Math.min(raw, Number(p.selectedQuantity) || 0)
                : Math.min(raw, maxQty);
          }
          if (!Number.isFinite(nextQty) || nextQty <= 0) {
            return p;
          }
          return {
            ...p,
            warehouseId: nextWarehouse,
            selectedQuantity: nextQty,
            selectedCoSub: recommendedCoSub,
          };
        })
      );
    },
    [
      balancesByProductId,
      multiWarehouseEnabled,
      preferredSaleWarehouseId,
      warehouses,
      creditReservedForEditLine,
      resolveLineAvailabilityCap,
      showSaleProfitPlusFields,
      pickRecommendedCoSubForLine,
      isEditing,
      effectiveApprovalState,
      warehouseLocked,
    ]
  );

  const updateSelectedCoSub = useCallback((id: string, coSub?: string) => {
    const nextSub = String(coSub ?? '').trim() || undefined;
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === id
          ? {
              ...p,
              selectedCoSub: nextSub,
            }
          : p
      )
    );
  }, []);

  const updatePrice = (id: string, price: number) => {
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === id
          ? {
              ...p,
              customPrice: price > 0 ? price : undefined,
            }
          : p
      )
    );
  };

  const updateDiscount = (id: string, discount: number) => {
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === id
          ? {
              ...p,
              discount: normalizeDiscountPercent(discount),
            }
          : p
      )
    );
  };

  const updateTax = (id: string, taxId?: string) => {
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === id
          ? {
              ...p,
              customTaxId: taxId,
            }
          : p
      )
    );
  };

  const resetPrice = (id: string) => {
    setSelectedProducts(prev =>
      prev.map(p =>
        p.id === id
          ? {
              ...p,
              customPrice: undefined,
            }
          : p
      )
    );
  };

  const canProceedStep1 = canProceedOrderFormStep1({
    selectedProducts,
    showSaleProfitPlusFields,
    profitReferenceDataReady,
    profitStep1Ready,
  });
  const canProceedStep2 = canProceedOrderFormStep2({ selectedClient });
  const selectedProductIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of selectedProducts) {
      if (p.id) ids.add(p.id);
      const catalogId = catalogProductIdFromLine(p);
      if (catalogId) ids.add(catalogId);
    }
    return ids;
  }, [selectedProducts]);

  const stockBalancesReadyForPersist = useMemo(() => {
    if (
      !multiWarehouseEnabled ||
      configurationFactors.allowNegativeInventory ||
      isProfitSyncRepair ||
      readOnly ||
      selectedProducts.length === 0 ||
      effectiveApprovalState === 'approved'
    ) {
      return true;
    }
    return selectedProducts.every(p => {
      const catalogId = catalogProductIdFromLine(p);
      if (!catalogId) return true;
      if (balancesLoadingProductIds[catalogId]) return false;
      return isProductStockBalancesLoaded(balancesByProductId[catalogId]);
    });
  }, [
    multiWarehouseEnabled,
    configurationFactors.allowNegativeInventory,
    isProfitSyncRepair,
    readOnly,
    selectedProducts,
    balancesByProductId,
    balancesLoadingProductIds,
    effectiveApprovalState,
  ]);

  const selectedLineStockWarnings = useMemo(
    () =>
      buildSelectedLineStockWarnings({
        showSaleProfitPlusFields,
        subAlmacenOptions,
        selectedProducts,
        formatNumber,
      }),
    [
      showSaleProfitPlusFields,
      subAlmacenOptions,
      selectedProducts,
      formatNumber,
    ]
  );

  const runPersistOrder = async (): Promise<boolean> => {
    if (isEditing && !orderServerHydrated) {
      addToast('Cargando datos de la venta… intente de nuevo en un momento.');
      return false;
    }
    const saveValidation = validateOrderFormForSave({
      selectedClient,
      selectedProducts,
      selectedVendorId,
    });
    if (!saveValidation.valid) {
      if (saveValidation.message) {
        addToast(saveValidation.message);
      }
      return false;
    }
    if (
      !isEditing &&
      !configurationFactors.allowNegativeInventory &&
      configurationFactors.blockSalesWhenInsufficientStock &&
      !isProfitSyncRepair &&
      !readOnly
    ) {
      for (const p of selectedProducts) {
        const sellable = resolveLineSellable(p, selectedProducts);
        const qty = Number(p.selectedQuantity) || 0;
        if (qty > sellable) {
          const label = p.name || p.code || 'producto';
          addToast(`Stock insuficiente para «${label}».`);
          return false;
        }
      }
    }
    const warehouseLockedOnSave =
      Boolean(isEditing && effectiveApprovalState === 'approved');
    if (
      multiWarehouseEnabled &&
      !warehouseLockedOnSave &&
      !isProfitSyncRepair &&
      !readOnly &&
      !warehouseId
    ) {
      addToast('Seleccione el almacén predeterminado para las líneas nuevas.');
      return false;
    }

    if (
      multiWarehouseEnabled &&
      !configurationFactors.allowNegativeInventory &&
      !isProfitSyncRepair &&
      !readOnly &&
      effectiveApprovalState !== 'approved'
    ) {
      const holdOrder = editHoldOrderRef.current || order;
      const gate = evaluateAggregatedStockGate({
        lines: selectedProducts,
        balancesByProductId,
        holdLines:
          isEditing && holdOrder?.approvalState === 'pending'
            ? holdOrder.products
            : undefined,
        holdHeaderWarehouseId: holdOrder?.warehouseId,
        headerWarehouseId: warehouseId,
        defaultWarehouseId,
      });
      if (!gate.ok) {
        if (gate.reason === 'balances_unloaded') {
          addToast(
            'Espere a que cargue el stock por almacén antes de guardar.'
          );
          return false;
        }
        const label =
          gate.line.name || gate.line.code || gate.productId || 'producto';
        addToast(
          gate.reason === 'missing_warehouse'
            ? `Seleccione almacén para «${label}».`
            : `Stock insuficiente para «${label}» en el almacén seleccionado.`
        );
        return false;
      }
    }

    let saleProfitIntegration: ProfitPlus2k8SaleIntegration | undefined;
    if (showSaleProfitPlusFields) {
      const sf = saleProfitFields;
      const fields: Record<string, string | number> = {};
      const trimStr = (v: unknown) =>
        typeof v === 'string' ? v.trim() : String(v ?? '').trim();
      if (sf.co_sucu != null && trimStr(sf.co_sucu)) {
        fields.co_sucu = trimStr(sf.co_sucu);
      }
      if (trimStr(resolvedSalesPersonCoVenForProfit)) {
        fields.co_ven = trimStr(resolvedSalesPersonCoVenForProfit);
      }
      if (sf.moneda != null && trimStr(sf.moneda)) {
        fields.moneda = trimStr(sf.moneda);
      }
      if (sf.tasa != null && Number.isFinite(Number(sf.tasa))) {
        fields.tasa = Number(sf.tasa);
      }
      if (sf.co_tran != null && trimStr(sf.co_tran)) {
        fields.co_tran = trimStr(sf.co_tran);
      }
      if (sf.forma_pag != null && trimStr(sf.forma_pag)) {
        fields.forma_pag = trimStr(sf.forma_pag);
      }
      if (Object.keys(fields).length > 0) {
        saleProfitIntegration = {
          fields: fields as ProfitPlusPedidoRecord,
          syncedAt: null,
        };
      }
    }

    if (isProfitSyncRepair && isEditing && order?.id) {
      const productsPayload = selectedProducts.map(p => {
        const price =
          p.customPrice !== undefined ? p.customPrice : Number(p.price) || 0;
        const { taxId, taxRate } = getOrderLineTaxForPersistence(p);
        const selectedCoSub = String(p.selectedCoSub ?? '').trim();
        return {
          productId: catalogProductIdFromLine(p) || p.id,
          code: p.code,
          name: p.name,
          brand: p.brand,
          description: p.description,
          price: price,
          quantity: Number(p.selectedQuantity) || 0,
          discount: normalizeDiscountPercent(p.discount),
          imageUrl: p.imageUrl,
          taxId: taxId,
          taxRate: taxRate,
          profitPlus2k8:
            showSaleProfitPlusFields && selectedCoSub
              ? {
                  fields: {
                    co_sub: selectedCoSub,
                    co_alma: selectedCoSub,
                  },
                  syncedAt: null,
                }
              : undefined,
        };
      });
      const linePatches = productsPayload
        .filter(p => p.profitPlus2k8)
        .map(p => ({
          productId: p.productId,
          profitPlus2k8: p.profitPlus2k8,
        }));
      if (!saleProfitIntegration && linePatches.length === 0) {
        addToast('No hay datos Profit para guardar.');
        return false;
      }
      showLoading('Guardando datos Profit...', 'overlay');
      try {
        await repairProfitPlusOrderExportServer(organizationId, order.id, {
          profitPlus2k8: saleProfitIntegration,
          products: linePatches.length > 0 ? linePatches : undefined,
        });
        hideLoading();
        let msg = 'Datos Profit guardados.';
        if (showSaleProfitPlusFields && profitEligibilityIssues.length > 0) {
          msg += ` Revisión Profit: ${profitEligibilityIssues.join('; ')}.`;
        }
        addToast(msg);
        onProfitSyncRepairSaved?.(order.id);
        return true;
      } catch (e: any) {
        console.log(e);
        addToast(
          e?.response?.data?.detail ||
            e?.message ||
            'Error al guardar datos Profit'
        );
        hideLoading();
        return false;
      }
    }

    showLoading(
      isEditing ? 'Guardando cambios...' : 'Creando venta...',
      'overlay'
    );
    try {
      const productsPayload = buildOrderProductsPayload(selectedProducts, {
        applyIva,
        taxConfig: configurationFactors,
        showSaleProfitPlusFields,
        // On edit, never invent line warehouses from the header — only persist
        // warehouses already on each line (including newly added lines).
        defaultWarehouseId: isEditing
          ? undefined
          : warehouseId || defaultWarehouseId || undefined,
      });

      const commonFields = buildOrderCommonFields({
        selectedClient: selectedClient!,
        deliveryAddress,
        creatorSalesPerson: resolveCreatorSalesPersonString(),
        paymentMethod,
        paymentCurrency,
        currency,
        creditEnabled,
        creditTermsDays,
        creditDueDate,
        totals,
        exchangeRate,
        comment,
        products: productsPayload,
        orderDiscountPercent: orderDiscountPercentRef.current,
        warehouseId:
          multiWarehouseEnabled && warehouseId ? warehouseId : undefined,
        saleProfitIntegration,
        emissionDate: isEditing ? undefined : emissionDate,
      });

      if (isEditing && order) {
        const payload: OrderUpdate = commonFields as any;
        await modifyOrderServer(organizationId, order.id, payload);
      } else {
        const payload: OrderCreate = {
          ...(commonFields as any),
          deleted: false,
          keywords: [],
        } as any;

        await createOrderServer(
          organizationId,
          payload as any,
          configurationFactors.orderApprovalRequired
        );
      }
      hideLoading();
      if (showSaleProfitPlusFields && profitEligibilityIssues.length > 0) {
        addToast(
          `Venta guardada. Revisión Profit Plus: ${profitEligibilityIssues.join('; ')}.`
        );
      }
      return true;
    } catch (e: any) {
      console.log(e);
      addToast(
        e?.response?.data?.detail || e?.message || 'Error al guardar la venta'
      );
      hideLoading();
      return false;
    }
  };

  const handleSubmit = async () => {
    const ok = await runPersistOrder();
    if (!ok) {
      // Keep confirmation dialog open; runPersistOrder already showed the reason.
      throw new Error('ORDER_FORM_SAVE_BLOCKED');
    }
    onSaved();
    onClose();
  };

  const persistProfitPedidoSyncChoice = async (
    skipPedidoSync: boolean
  ): Promise<void> => {
    if (!order?.id) return;
    const fresh = await getOrderServer(organizationId, order.id);
    const existing = fresh.profitPlus2k8;
    await modifyOrderServer(organizationId, order.id, {
      profitPlus2k8: {
        ...(existing ?? {}),
        fields: (existing?.fields ?? {}) as ProfitPlusPedidoRecord,
        syncedAt: existing?.syncedAt ?? null,
        skipPedidoSync,
      },
    });
  };

  const approveOrderAfterProfitChoice = async (
    skipPedidoSync: boolean,
    assessment?: ProfitOrderSyncAssessment
  ) => {
    if (!order?.id) {
      throw new Error('ORDER_APPROVE_INVALID_STATE');
    }
    if (showSaleProfitPlusFields) {
      await persistProfitPedidoSyncChoice(skipPedidoSync);
    }
    showLoading('Aprobando venta...', 'overlay');
    try {
      await approveOrderServer(organizationId, order.id);
      hideLoading();
      if (skipPedidoSync && showSaleProfitPlusFields) {
        const msg = buildProfitSyncAssessmentMessage(
          assessment ?? { blockers: [], warnings: [] }
        );
        addToast(
          msg
            ? `Venta aprobada. ${msg}`
            : 'Venta aprobada sin crear pedido en Profit Plus.'
        );
      } else {
        addToast('Venta aprobada');
        onApprovalReviewSuccess?.(order.id);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      console.log(e);
      addToast(
        e?.response?.data?.detail || e?.message || 'Error al aprobar la venta'
      );
      hideLoading();
    }
  };

  const handleApproveFromReviewFlow = async () => {
    if (isProfitSyncRepair) {
      throw new Error('ORDER_APPROVE_INVALID_STATE');
    }
    if (!approvalReviewMode || !order?.id) {
      throw new Error('ORDER_APPROVE_INVALID_STATE');
    }
    const ok = await runPersistOrder();
    if (!ok) {
      throw new Error('ORDER_FORM_SAVE_BLOCKED');
    }
    if (!showSaleProfitPlusFields) {
      await approveOrderAfterProfitChoice(false);
      return;
    }

    const fresh = await getOrderServer(organizationId, order.id);
    const assessment = await collectProfitOrderSyncAssessment(
      organizationId,
      fresh
    );
    setProfitSyncBlockers(assessment.blockers);
    setProfitSyncWarnings(assessment.warnings);
    setProfitEligibilityIssues([
      ...assessment.blockers,
      ...assessment.warnings,
    ]);

    if (assessment.blockers.length > 0) {
      await approveOrderAfterProfitChoice(true, assessment);
      return;
    }

    if (profitPlus2k8Config?.askBeforeCreatingProfitPedido === false) {
      await approveOrderAfterProfitChoice(false, assessment);
      return;
    }

    const details = buildProfitSyncAssessmentMessage(assessment);
    // Defer opening this dialog until after the parent "Aprobar venta" confirmation
    // closes: ConfirmationDialog always calls closeDialog() after await onConfirm(), which
    // would immediately clear a synchronously opened second modal.
    window.setTimeout(() => {
      showInfoConfirmation(
        'Crear pedido en Profit Plus',
        [
          'La venta ya cumple los requisitos mínimos para aprobarse en MayorX.',
          '¿Desea crear el pedido en Profit Plus 2k8 al aprobar?',
          details,
        ]
          .filter(Boolean)
          .join('\n\n'),
        () => approveOrderAfterProfitChoice(false, assessment),
        {
          confirmText: 'Aprobar y crear pedido',
          cancelText: 'Aprobar sin crear pedido',
          onCancel: () => approveOrderAfterProfitChoice(true, assessment),
        }
      );
    }, 0);
  };

  const handleApproveReviewConfirm = () => {
    showInfoConfirmation(
      'Aprobar venta',
      'Se guardarán los cambios pendientes y se aprobará esta venta. ¿Continuar?',
      handleApproveFromReviewFlow,
      {
        confirmText: 'Aprobar',
        cancelText: 'Cancelar',
      }
    );
  };

  const handleSaveSale = () => {
    showInfoConfirmation(
      isEditing ? 'Guardar Cambios' : 'Crear Venta',
      isEditing
        ? '¿Estás seguro de que quieres guardar los cambios de esta venta?'
        : '¿Estás seguro de que quieres crear esta venta?',
      async () => {
        const ok = await runPersistOrder();
        if (!ok) {
          throw new Error('ORDER_FORM_SAVE_BLOCKED');
        }
        onSaved();
        onClose();
      },
      {
        confirmText: isEditing ? 'Guardar Cambios' : 'Crear Venta',
        cancelText: 'Cancelar',
      }
    );
  };

  const handleProfitRepairSaveConfirm = () => {
    showInfoConfirmation(
      'Guardar datos Profit Plus',
      'Se actualizarán solo los campos de exportación al ERP (cabecera del pedido y subalmacén por línea). Las cantidades, precios y cliente aprobados no cambian. ¿Continuar?',
      async () => {
        const ok = await runPersistOrder();
        if (ok) {
          onSaved();
          onClose();
        }
      },
      {
        confirmText: 'Guardar',
        cancelText: 'Cancelar',
      }
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {readOnly
            ? 'Ver Orden'
            : isProfitSyncRepair
              ? 'Completar datos Profit Plus (venta aprobada)'
              : approvalReviewMode && order
                ? 'Revisar y aprobar venta'
                : order
                  ? 'Editar Venta'
                  : 'Crear Venta'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        {isProfitSyncRepair && isEditing && (
          <Alert variant="info" className="mb-3">
            Esta venta ya está <strong>aprobada</strong>. Aquí solo puede
            completar los datos que necesita el puente para{' '}
            <strong>exportar el pedido a Profit Plus</strong> (sucursal,
            condiciones, subalmacén por línea, vendedor ERP). No se modifican
            cantidades ni precios.
          </Alert>
        )}
        {approvalReviewMode && isEditing && !isProfitSyncRepair && (
          <Alert variant="info" className="mb-3">
            {showSaleProfitPlusFields ? (
              <>
                Revise el detalle de la venta y los campos de Profit Plus.
                «Aprobar venta» guardará primero los cambios pendientes y luego
                aprobará la venta.
              </>
            ) : (
              <>
                Revise el detalle de la venta. «Aprobar venta» guardará primero
                los cambios pendientes y luego aprobará la venta.
              </>
            )}
          </Alert>
        )}
        {showSaleProfitPlusFields && profitEligibilityIssues.length > 0 && (
          <Alert variant="warning" className="mb-3">
            <strong>Revisión para Profit Plus.</strong> Los bloqueos impiden
            crear el pedido en Profit; los avisos permiten continuar y se podrán
            corregir después si hace falta.
            <ul className="mb-0 mt-2 small">
              {profitSyncBlockers.map((msg, i) => (
                <li key={`blocker-${i}`}>
                  <strong>Bloqueo:</strong> {msg}
                </li>
              ))}
              {profitSyncWarnings.map((msg, i) => (
                <li key={`warning-${i}`}>
                  <strong>Aviso:</strong> {msg}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant={step === 1 ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setStep(1)}
            >
              1. Productos
            </Button>
            <Button
              variant={step === 2 ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
            >
              2. Cliente
            </Button>
            <Button
              variant={step === 3 ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setStep(3)}
              disabled={!canProceedStep1 || !canProceedStep2}
            >
              3. Vista general
            </Button>
          </div>
        </div>

        {step === 1 && (
          <>
            {showSaleProfitPlusFields && (
              <Card className="mb-3 border-secondary">
                <Card.Header className="py-2 small">
                  1 — Profit Plus 2k8 (obligatorio antes de productos)
                </Card.Header>
                <Card.Body className="py-2">
                  <p className="text-muted small mb-2">
                    Defina la sucursal y las condiciones del pedido en el ERP.
                    La columna disponible del catálogo muestra el inventario
                    MayorX del producto; el sub-almacén por línea sigue
                    aplicando al exportar a Profit.
                  </p>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className="mb-3"
                    onClick={applySuggestionFromOrgProfitConfig}
                  >
                    Sugerir desde configuración
                  </Button>
                  <Row className="g-2">
                    <Col md={6} lg={4}>
                      <Form.Group>
                        <Form.Label className="small">
                          Almacén / sucursal (co_sucu)
                        </Form.Label>
                        {referenceTables?.almacenes &&
                        referenceTables.almacenes.length > 0 ? (
                          <Form.Select
                            size="sm"
                            value={String(saleProfitFields.co_sucu ?? '')}
                            onChange={e =>
                              setSaleProfitFields(p => ({
                                ...p,
                                co_sucu: e.target.value.trim() || undefined,
                              }))
                            }
                          >
                            {referenceTables.almacenes.map(
                              (a: { co_alma?: string; alma_des?: string }) => {
                                const code = String(a.co_alma ?? '').trim();
                                if (!code) return null;
                                return (
                                  <option key={code} value={code}>
                                    {code} —{' '}
                                    {String(a.alma_des ?? '').trim() || '—'}
                                  </option>
                                );
                              }
                            )}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            size="sm"
                            placeholder="Ej. 01"
                            value={String(saleProfitFields.co_sucu ?? '')}
                            onChange={e =>
                              setSaleProfitFields(p => ({
                                ...p,
                                co_sucu: e.target.value.trim() || undefined,
                              }))
                            }
                          />
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={4}>
                      <Form.Group>
                        <Form.Label className="small">
                          Moneda ERP (moneda)
                        </Form.Label>
                        {!profitReferenceDataReady ? (
                          <div className="d-flex align-items-center gap-2 text-muted small py-1">
                            <Spinner animation="border" size="sm" />
                            Cargando tablas de referencia…
                          </div>
                        ) : profitUsdMonedaCode ? (
                          <Form.Select
                            size="sm"
                            value={profitUsdMonedaCode}
                            disabled
                          >
                            <option value={profitUsdMonedaCode}>
                              {(() => {
                                const m = referenceTables?.monedas?.find(
                                  x =>
                                    String(
                                      (x as { co_mone?: string }).co_mone ?? ''
                                    ).trim() === profitUsdMonedaCode
                                ) as
                                  | { co_mone?: string; mone_des?: string }
                                  | undefined;
                                const des = String(m?.mone_des ?? '').trim();
                                return `${profitUsdMonedaCode} — ${des || 'Dólar / USD'}`;
                              })()}
                            </option>
                          </Form.Select>
                        ) : (
                          <>
                            <Form.Control
                              size="sm"
                              value=""
                              readOnly
                              plaintext={false}
                              className="text-danger"
                              placeholder="—"
                            />
                            <Form.Text className="text-danger">
                              No se encontró moneda dólar/USD en las tablas de
                              referencia (dbo.moneda). Sincronice monedas desde
                              Profit en Configuración → Integraciones.
                            </Form.Text>
                          </>
                        )}
                        {profitReferenceDataReady && profitUsdMonedaCode && (
                          <Form.Text className="text-muted">
                            Política: solo dólares; el código{' '}
                            <code>{profitUsdMonedaCode}</code> debe existir en
                            el ERP (validado arriba).
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={4}>
                      <Form.Group>
                        <Form.Label className="small">Tasa (tasa)</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          readOnly
                          value={formatExchangeRate(exchangeRate)}
                        />
                        <Form.Text className="text-muted">
                          Se usa la tasa activa de la venta ({referenceCurrency}
                          →{currency}).
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={4}>
                      <Form.Group>
                        <Form.Label className="small">
                          Transportista (co_tran → dbo.transpor)
                        </Form.Label>
                        {referenceTables?.transportistas &&
                        referenceTables.transportistas.length > 0 ? (
                          <Form.Select
                            size="sm"
                            value={String(saleProfitFields.co_tran ?? '')}
                            onChange={e =>
                              setSaleProfitFields(p => ({
                                ...p,
                                co_tran: e.target.value.trim() || undefined,
                              }))
                            }
                          >
                            {[...referenceTables.transportistas]
                              .sort((a, b) =>
                                String(a.des_tran ?? '').localeCompare(
                                  String(b.des_tran ?? ''),
                                  'es'
                                )
                              )
                              .map(
                                (
                                  t: { co_tran?: string; des_tran?: string },
                                  i: number
                                ) => {
                                  const code = String(t.co_tran ?? '').trim();
                                  if (!code) return null;
                                  return (
                                    <option key={`${code}-${i}`} value={code}>
                                      {code} —{' '}
                                      {String(t.des_tran ?? '').trim() || '—'}
                                    </option>
                                  );
                                }
                              )}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            size="sm"
                            placeholder="Ej. 01"
                            value={String(saleProfitFields.co_tran ?? '')}
                            onChange={e =>
                              setSaleProfitFields(p => ({
                                ...p,
                                co_tran: e.target.value.trim() || undefined,
                              }))
                            }
                          />
                        )}
                        {(!referenceTables?.transportistas ||
                          referenceTables.transportistas.length === 0) && (
                          <Form.Text className="text-muted">
                            Sincronice tablas de referencia desde el puente para
                            listar transportistas.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={4}>
                      <Form.Group>
                        <Form.Label className="small">
                          Condición de pago (forma_pag → co_cond en
                          dbo.condicio)
                        </Form.Label>
                        {referenceTables?.condiciones &&
                        referenceTables.condiciones.length > 0 ? (
                          <Form.Select
                            size="sm"
                            value={String(saleProfitFields.forma_pag ?? '')}
                            onChange={e =>
                              setSaleProfitFields(p => ({
                                ...p,
                                forma_pag: e.target.value.trim() || undefined,
                              }))
                            }
                          >
                            {[...referenceTables.condiciones]
                              .sort(
                                (a, b) =>
                                  (Number(a.dias_cred) || 0) -
                                    (Number(b.dias_cred) || 0) ||
                                  String(a.cond_des ?? '').localeCompare(
                                    String(b.cond_des ?? ''),
                                    'es'
                                  )
                              )
                              .map(
                                (
                                  c: {
                                    co_cond?: string;
                                    cond_des?: string;
                                    dias_cred?: number;
                                  },
                                  i: number
                                ) => {
                                  const code = String(c.co_cond ?? '').trim();
                                  if (!code) return null;
                                  return (
                                    <option key={`${code}-${i}`} value={code}>
                                      {code} —{' '}
                                      {String(c.cond_des ?? '').trim() || '—'}
                                      {c.dias_cred != null &&
                                      Number.isFinite(Number(c.dias_cred))
                                        ? ` (${c.dias_cred} d.)`
                                        : ''}
                                    </option>
                                  );
                                }
                              )}
                          </Form.Select>
                        ) : (
                          <Form.Control
                            size="sm"
                            placeholder="Ej. 02"
                            value={String(saleProfitFields.forma_pag ?? '')}
                            onChange={e =>
                              setSaleProfitFields(p => ({
                                ...p,
                                forma_pag: e.target.value.trim() || undefined,
                              }))
                            }
                          />
                        )}
                        {(!referenceTables?.condiciones ||
                          referenceTables.condiciones.length === 0) && (
                          <Form.Text className="text-muted">
                            Sincronice tablas de referencia para listar
                            condiciones de pago.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  {effectiveCoSucu && (
                    <p className="small text-muted mb-0 mt-2">
                      Sub-almacén para stock:{' '}
                      <strong>{coSubForSelectedAlmacen ?? '—'}</strong>
                      {referenceTables?.subAlmacenes &&
                        referenceTables.subAlmacenes.length === 0 && (
                          <span className="ms-1">
                            (sincronice «sub_almacenes» desde Profit en la
                            configuración del puente para mapeo exacto)
                          </span>
                        )}
                    </p>
                  )}
                </Card.Body>
              </Card>
            )}
            {showSaleProfitPlusFields &&
              profitReferenceDataReady &&
              !profitUsdMonedaCode && (
                <Alert variant="danger" className="mb-2 py-2 small">
                  No hay una moneda <strong>dólar / USD</strong> reconocible en{' '}
                  <strong>dbo.moneda</strong> (tablas de referencia). Sincronice
                  monedas desde Profit en{' '}
                  <strong>Configuración → Integraciones</strong> antes de armar
                  el pedido; de lo contrario el export puede usar un{' '}
                  <code>co_mone</code> inválido.
                </Alert>
              )}
            {showSaleProfitPlusFields &&
              profitReferenceDataReady &&
              profitUsdMonedaCode &&
              !profitStep1Ready && (
                <Alert variant="warning" className="mb-2 py-2 small">
                  Complete el bloque <strong>Profit Plus</strong> (sucursal,
                  tasa, transacción y forma de pago). La moneda queda fijada al
                  código USD válido del ERP (<code>{profitUsdMonedaCode}</code>
                  ).
                </Alert>
              )}
            <div className={formStyles.hoverSplitPanels}>
              {!isProfitSyncRepair && (
                <div
                  className={`${formStyles.hoverSplitPane} ${formStyles.hoverSplitPaneLeft}`}
                >
                  <Card className="h-100">
                    <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                      <span className="small fw-semibold">
                        Catálogo de productos
                      </span>
                      <span className="small text-muted">
                        {products.length} resultado(s)
                      </span>
                    </Card.Header>
                    <Card.Body style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                      {isEditing &&
                      !shouldShowProductCatalog &&
                      !isProfitSyncRepair ? (
                        <div className="alert alert-secondary mb-0">
                          <div className="mb-2">
                            Estás editando una venta existente. Tus productos
                            seleccionados ya se muestran en el panel derecho.
                            Para agregar productos adicionales, puedes cargar el
                            catálogo completo.
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              setShouldShowProductCatalog(true);
                              fetchProducts();
                            }}
                          >
                            Cargar catálogo para agregar
                          </Button>
                        </div>
                      ) : !showSaleProfitPlusFields ||
                        (profitReferenceDataReady && profitStep1Ready) ? (
                        <fieldset
                          style={{ border: 'none', margin: 0, padding: 0 }}
                        >
                          <InputGroup className="mb-2">
                            <Form.Control
                              placeholder="Buscar productos"
                              value={productSearch}
                              disabled={
                                showSaleProfitPlusFields &&
                                (!profitReferenceDataReady || !profitStep1Ready)
                              }
                              onChange={e =>
                                setProductSearch(e.currentTarget.value)
                              }
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  fetchProducts();
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline-primary"
                              onClick={fetchProducts}
                              disabled={
                                showSaleProfitPlusFields &&
                                (!profitReferenceDataReady || !profitStep1Ready)
                              }
                            >
                              <FiSearch />
                            </Button>
                          </InputGroup>
                          {productPending ? (
                            <Spinner animation="border" />
                          ) : (
                            <div className="table-responsive">
                              <Table
                                hover
                                size="sm"
                                className={`mb-0 ${formStyles.catalogCompactTable} ${formStyles.fixedActionColumnTable}`}
                              >
                                <thead>
                                  <tr>
                                    <th>Código</th>
                                    <th>Nombre</th>
                                    <th>Precio</th>
                                    <th>Impuesto</th>
                                    <th>Disp.</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {products.map(p => (
                                    <tr key={p.id}>
                                      <td
                                        className={formStyles.catalogCodeCell}
                                      >
                                        {p.code}
                                      </td>
                                      <td>
                                        <div
                                          className={formStyles.catalogNameCell}
                                        >
                                          {p.name}
                                        </div>
                                        {p.brand ? (
                                          <div
                                            className={
                                              formStyles.catalogBrandCell
                                            }
                                          >
                                            {p.brand}
                                          </div>
                                        ) : null}
                                      </td>
                                      <td>
                                        {formatMoney(
                                          Number(p.price) || 0,
                                          referenceCurrency || currency
                                        )}
                                      </td>
                                      <td>
                                        {(() => {
                                          const info = getProductTaxInfo({
                                            ...p,
                                            selectedQuantity: 1,
                                          } as SelectedProduct);
                                          return info.taxName
                                            ? `${info.taxName} (${formatNumber(info.taxRate)}%)`
                                            : 'Sin impuesto';
                                        })()}
                                      </td>
                                      <td>
                                        {multiWarehouseEnabled ? (
                                          isProductBalancesLoading(p.id) ? (
                                            <span className="text-muted small">
                                              Cargando…
                                            </span>
                                          ) : (
                                            <div
                                              title={formatWarehouseStockHint(
                                                balancesByProductId[p.id],
                                                warehouses,
                                                preferredSaleWarehouseId
                                              )}
                                            >
                                              <div>
                                                {formatNumber(
                                                  resolveCatalogPickerSellable(p)
                                                )}
                                              </div>
                                              {isProductStockBalancesLoaded(
                                                balancesByProductId[p.id]
                                              ) ? (
                                                <div className="small text-muted">
                                                  Total:{' '}
                                                  {formatNumber(
                                                    totalSellableAcrossWarehouses(
                                                      balancesByProductId[p.id]
                                                    )
                                                  )}
                                                </div>
                                              ) : null}
                                            </div>
                                          )
                                        ) : (
                                          formatNumber(
                                            resolveCatalogAvailability(p)
                                          )
                                        )}
                                      </td>
                                      <td>
                                        <Button
                                          size="sm"
                                          variant={
                                            selectedProductIds.has(p.id)
                                              ? 'outline-secondary'
                                              : 'outline-primary'
                                          }
                                          onClick={() => addProduct(p)}
                                          disabled={
                                            selectedProductIds.has(p.id) ||
                                            (showSaleProfitPlusFields &&
                                              (!profitReferenceDataReady ||
                                                !profitStep1Ready))
                                          }
                                        >
                                          {selectedProductIds.has(p.id)
                                            ? 'Agregado'
                                            : 'Agregar'}
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                  {productHasMore && (
                                    <tr>
                                      <td
                                        colSpan={6}
                                        className="text-center py-2"
                                      >
                                        <Button
                                          size="sm"
                                          variant="outline-secondary"
                                          onClick={loadMoreProducts}
                                          disabled={productLoadMorePending}
                                        >
                                          {productLoadMorePending ? (
                                            <Spinner
                                              animation="border"
                                              size="sm"
                                              className="me-1"
                                            />
                                          ) : null}
                                          Cargar más productos
                                        </Button>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          )}
                        </fieldset>
                      ) : (
                        <div className="small text-muted">
                          Complete primero los campos de Profit Plus para
                          habilitar la selección de productos.
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              )}
              <div
                className={`${formStyles.hoverSplitPane} ${isProfitSyncRepair ? formStyles.hoverSplitPaneFull : formStyles.hoverSplitPaneRight}`}
              >
                <Card className="h-100">
                  <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                    <span className="small fw-semibold">Seleccionados</span>
                    <span className="small text-muted">
                      {selectedProducts.length} producto(s)
                    </span>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    {selectedProducts.length > 0 ? (
                      <>
                        {selectedProducts.map(p => {
                          const currentPrice =
                            p.customPrice !== undefined
                              ? p.customPrice
                              : Number(p.price) || 0;
                          const totalPrice = computeOrderLineSubtotal(
                            p,
                            orderDiscountPercent
                          );
                          const requestedQty = Number(p.selectedQuantity) || 0;
                          const selectedSub = String(
                            p.selectedCoSub ?? ''
                          ).trim();
                          const selectedSubAvailable =
                            resolveAvailableQtyAtCoSub(p, selectedSub);
                          const maxSubAvailable =
                            resolveMaxAvailableQtyAcrossSubs(
                              p,
                              subAlmacenOptions
                            );
                          const hasEnoughSomewhere =
                            typeof maxSubAvailable === 'number' &&
                            maxSubAvailable >= requestedQty;
                          const selectedSubInsufficient =
                            requestedQty > 0 &&
                            typeof selectedSubAvailable === 'number' &&
                            selectedSubAvailable < requestedQty &&
                            hasEnoughSomewhere;
                          const orderedSubOptions =
                            subAlmacenOptionsIncludingSavedSelection(
                              subAlmacenOptions,
                              p.selectedCoSub
                            );
                          const productTaxInfo = getProductTaxInfo(p);
                          const catalogId = String(p.productId || p.id || '').trim();
                          const lineBalances = balancesByProductId[catalogId];
                          const lineBalancesLoaded = isProductStockBalancesLoaded(
                            lineBalances
                          );
                          const lineBalancesLoading =
                            isProductBalancesLoading(catalogId);
                          const lineWarehouseId =
                            p.warehouseId ||
                            preferredSaleWarehouseId ||
                            '';
                          const creditAtLine = creditReservedForEditLine(
                            catalogId,
                            lineWarehouseId
                          );
                          const siblingDemand = siblingDemandAtWarehouse(
                            selectedProducts,
                            p.id,
                            catalogId,
                            lineWarehouseId,
                            warehouseId,
                            defaultWarehouseId
                          );
                          const linePoolSellable = lineSellableWithSharedPool(
                            lineBalances,
                            lineWarehouseId,
                            creditAtLine,
                            siblingDemand
                          );
                          const warehouseStockInsufficient =
                            multiWarehouseEnabled &&
                            lineBalancesLoaded &&
                            !lineBalancesLoading &&
                            requestedQty > 0 &&
                            linePoolSellable < requestedQty;
                          const warehouseStockAvailableElsewhere =
                            warehouseStockInsufficient &&
                            hasSufficientStockSomewhere(
                              lineBalances,
                              requestedQty,
                              creditAtLine
                                ? { [lineWarehouseId]: creditAtLine }
                                : undefined
                            );
                          return (
                            <Card key={p.id} className="mb-2">
                              <Card.Body className="py-1 px-2">
                                <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                  <div className="d-flex align-items-start gap-2">
                                    {p.imageUrl ? (
                                      <img
                                        src={p.imageUrl}
                                        alt={p.name}
                                        style={{
                                          width: '40px',
                                          height: '40px',
                                          objectFit: 'cover',
                                          borderRadius: '4px',
                                        }}
                                        onError={e => {
                                          e.currentTarget.style.display =
                                            'none';
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: '40px',
                                          height: '40px',
                                          backgroundColor:
                                            'var(--bs-tertiary-bg, var(--bs-body-bg))',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color:
                                            'var(--bs-secondary-color, var(--bs-body-color))',
                                          border:
                                            '1px solid var(--bs-border-color)',
                                        }}
                                        title="Producto sin imagen"
                                      >
                                        <FiImage size={15} />
                                      </div>
                                    )}
                                    <div>
                                      <div className="fw-semibold">
                                        {p.name}
                                      </div>
                                      <div className="small text-muted">
                                        {p.code}
                                        {p.brand ? ` · ${p.brand}` : ''}
                                        {' · '}
                                        Disp.{' '}
                                        {lineBalancesLoading ? (
                                          <span className="text-muted">
                                            Cargando stock…
                                          </span>
                                        ) : (
                                          formatNumber(
                                            multiWarehouseEnabled
                                              ? resolveLineSellable(p)
                                              : resolveCatalogAvailability(p)
                                          )
                                        )}
                                        {multiWarehouseEnabled &&
                                        lineBalancesLoaded &&
                                        !lineBalancesLoading ? (
                                          <span
                                            className="d-block"
                                            title={formatWarehouseStockHint(
                                              lineBalances,
                                              warehouses,
                                              lineWarehouseId
                                            )}
                                          >
                                            {formatWarehouseStockHint(
                                              lineBalances,
                                              warehouses,
                                              lineWarehouseId
                                            )}
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => removeProduct(p.id)}
                                    title="Quitar producto"
                                    disabled={isProfitSyncRepair}
                                  >
                                    <FiTrash2 />
                                  </Button>
                                </div>

                                <Row className="g-1">
                                  <Col md={6}>
                                    <Form.Label className="small mb-0">
                                      Precio personalizado
                                    </Form.Label>
                                    <InputGroup size="sm">
                                      <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={
                                          p.customPrice !== undefined
                                            ? p.customPrice
                                            : ''
                                        }
                                        onChange={e =>
                                          updatePrice(
                                            p.id,
                                            Number(e.currentTarget.value)
                                          )
                                        }
                                        placeholder={formatNumber(
                                          Number(p.price) || 0
                                        )}
                                        disabled={isProfitSyncRepair}
                                      />
                                      {p.customPrice !== undefined && (
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={() => resetPrice(p.id)}
                                          title="Restaurar precio original"
                                        >
                                          ↺
                                        </Button>
                                      )}
                                    </InputGroup>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Label className="small mb-0">
                                      Cantidad
                                    </Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      inputMode="decimal"
                                      min={0}
                                      step="any"
                                      max={resolveLineAvailabilityCap(p)}
                                      value={
                                        quantityDraftByProductId[p.id] ??
                                        String(p.selectedQuantity)
                                      }
                                      onFocus={() =>
                                        setQuantityDraftByProductId(prev => ({
                                          ...prev,
                                          [p.id]: String(p.selectedQuantity),
                                        }))
                                      }
                                      onChange={e => {
                                        const raw = e.currentTarget.value;
                                        setQuantityDraftByProductId(prev => ({
                                          ...prev,
                                          [p.id]: raw,
                                        }));
                                        const trimmed = raw.trim();
                                        if (trimmed === '') return;
                                        const n = parseFloat(trimmed);
                                        if (Number.isFinite(n) && n > 0) {
                                          commitQuantity(p.id, n);
                                        }
                                      }}
                                      onBlur={e => {
                                        const raw = e.currentTarget.value;
                                        setQuantityDraftByProductId(prev => {
                                          const next = { ...prev };
                                          delete next[p.id];
                                          return next;
                                        });
                                        const n = parseFloat(raw.trim());
                                        if (Number.isFinite(n) && n > 0) {
                                          commitQuantity(p.id, n);
                                        }
                                      }}
                                      disabled={isProfitSyncRepair}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Form.Label className="small mb-0">
                                      Descuento (%)
                                    </Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      max={100}
                                      step="0.01"
                                      inputMode="decimal"
                                      value={
                                        discountDraftByProductId[p.id] ??
                                        String(
                                          normalizeDiscountPercent(p.discount)
                                        )
                                      }
                                      onFocus={() =>
                                        setDiscountDraftByProductId(prev => ({
                                          ...prev,
                                          [p.id]: String(
                                            normalizeDiscountPercent(p.discount)
                                          ),
                                        }))
                                      }
                                      onChange={e => {
                                        const raw = e.currentTarget.value;
                                        setDiscountDraftByProductId(prev => ({
                                          ...prev,
                                          [p.id]: raw,
                                        }));
                                        const trimmed = raw.trim();
                                        if (trimmed === '') return;
                                        const n = parseFloat(trimmed);
                                        if (Number.isFinite(n)) {
                                          updateDiscount(p.id, n);
                                        }
                                      }}
                                      onBlur={e => {
                                        const raw = e.currentTarget.value;
                                        setDiscountDraftByProductId(prev => {
                                          const next = { ...prev };
                                          delete next[p.id];
                                          return next;
                                        });
                                        const trimmed = raw.trim();
                                        if (trimmed === '') {
                                          updateDiscount(p.id, 0);
                                          return;
                                        }
                                        const n = parseFloat(trimmed);
                                        updateDiscount(
                                          p.id,
                                          Number.isFinite(n) ? n : 0
                                        );
                                      }}
                                      disabled={isProfitSyncRepair}
                                    />
                                  </Col>
                                  {multiWarehouseEnabled ? (
                                    <Col md={6}>
                                      <Form.Label className="small mb-0">
                                        Almacén
                                      </Form.Label>
                                      <Form.Select
                                        size="sm"
                                        value={p.warehouseId || ''}
                                        onChange={e => {
                                          const nextWarehouseId =
                                            e.currentTarget.value;
                                          setSelectedProducts(prev =>
                                            prev.map(sp =>
                                              sp.id === p.id
                                                ? {
                                                    ...sp,
                                                    warehouseId:
                                                      nextWarehouseId ||
                                                      undefined,
                                                  }
                                                : sp
                                            )
                                          );
                                        }}
                                        disabled={
                                          warehouseLocked ||
                                          readOnly ||
                                          isProfitSyncRepair ||
                                          warehouses.length === 0
                                        }
                                      >
                                        <option value="">
                                          Seleccione almacén
                                        </option>
                                        {warehouses.map(warehouse => (
                                          <option
                                            key={warehouse.id}
                                            value={warehouse.id}
                                          >
                                            {warehouse.name}
                                            {warehouse.isDefault
                                              ? ' (predeterminado)'
                                              : ''}
                                          </option>
                                        ))}
                                      </Form.Select>
                                      {lineBalancesLoading ? (
                                        <small className="text-muted d-block mt-1">
                                          Consultando inventario por almacén…
                                        </small>
                                      ) : null}
                                      {warehouseStockInsufficient &&
                                      !hasSufficientStockSomewhere(
                                        lineBalances,
                                        requestedQty,
                                        creditAtLine
                                          ? { [lineWarehouseId]: creditAtLine }
                                          : undefined
                                      ) ? (
                                        <small className="text-danger d-block mt-1">
                                          No hay almacén con inventario
                                          suficiente para{' '}
                                          {formatNumber(requestedQty)}.
                                        </small>
                                      ) : null}
                                      {warehouseStockAvailableElsewhere ? (
                                        <small className="text-warning d-block mt-1">
                                          Este almacén no cubre la cantidad.
                                          Cambie el almacén de la línea o el
                                          almacén principal de la venta.
                                        </small>
                                      ) : null}
                                    </Col>
                                  ) : null}
                                  <Col md={6}>
                                    <Form.Label className="small mb-0">
                                      Impuesto
                                    </Form.Label>
                                    <Form.Select
                                      size="sm"
                                      value={p.customTaxId ?? ''}
                                      onChange={e =>
                                        updateTax(
                                          p.id,
                                          e.currentTarget.value || undefined
                                        )
                                      }
                                      disabled={isProfitSyncRepair}
                                    >
                                      <option value="">
                                        Predeterminado del producto
                                      </option>
                                      {(configurationFactors.taxes || [])
                                        .filter(tax => tax.enabled)
                                        .map(tax => (
                                          <option key={tax.id} value={tax.id}>
                                            {tax.name} ({tax.rate}%)
                                          </option>
                                        ))}
                                    </Form.Select>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Label className="small mb-0">
                                      Subalmacén
                                    </Form.Label>
                                    {showSaleProfitPlusFields &&
                                    (subAlmacenOptions.length > 0 ||
                                      selectedSub) ? (
                                      <>
                                        <Form.Select
                                          size="sm"
                                          value={selectedSub}
                                          onChange={e =>
                                            updateSelectedCoSub(
                                              p.id,
                                              e.currentTarget.value || undefined
                                            )
                                          }
                                        >
                                          <option value="">
                                            Seleccionar subalmacén
                                          </option>
                                          {orderedSubOptions.map(opt => (
                                            <option
                                              key={opt.coSub}
                                              value={opt.coSub}
                                            >
                                              {opt.label}
                                            </option>
                                          ))}
                                        </Form.Select>
                                        {requestedQty > 0 &&
                                          typeof maxSubAvailable === 'number' &&
                                          maxSubAvailable < requestedQty && (
                                            <small className="text-danger d-block mt-1">
                                              No hay subalmacén con inventario
                                              suficiente para{' '}
                                              {formatNumber(requestedQty)}.
                                            </small>
                                          )}
                                        {selectedSubInsufficient && (
                                          <small className="text-warning d-block mt-1">
                                            Este subalmacén no cubre la
                                            cantidad. Hay otro con
                                            disponibilidad.
                                          </small>
                                        )}
                                      </>
                                    ) : (
                                      <div className="small text-muted pt-1">
                                        —
                                      </div>
                                    )}
                                  </Col>
                                </Row>

                                <div className="d-flex justify-content-end mt-1">
                                  <span
                                    className={
                                      p.customPrice !== undefined
                                        ? 'text-primary fw-bold'
                                        : 'fw-semibold'
                                    }
                                  >
                                    Total línea:{' '}
                                    {formatMoney(
                                      totalPrice,
                                      referenceCurrency || currency
                                    )}
                                  </span>
                                </div>
                              </Card.Body>
                            </Card>
                          );
                        })}
                      </>
                    ) : (
                      <div className="text-center text-muted py-3">
                        Sin productos seleccionados
                      </div>
                    )}

                    {selectedLineStockWarnings.length > 0 && (
                      <Alert variant="warning" className="py-2 small">
                        <div className="fw-semibold mb-1">
                          Algunos productos no tienen subalmacén con inventario
                          suficiente:
                        </div>
                        {selectedLineStockWarnings.map(w => (
                          <div key={w.id}>{w.message}</div>
                        ))}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {isProfitSyncRepair ? (
              <Alert variant="secondary" className="mb-0">
                El <strong>cliente</strong> de esta venta ya está fijado (venta
                aprobada). Use el paso 3 para el vendedor ERP si necesita
                cambiar <code>co_ven</code>.
              </Alert>
            ) : (
              <>
                <InputGroup className="mb-2">
                  <Form.Control
                    placeholder="Buscar cliente"
                    value={clientSearch}
                    onChange={e => setClientSearch(e.currentTarget.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        fetchClients();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={fetchClients}
                  >
                    <FiSearch />
                  </Button>
                </InputGroup>
                {clientPending ? (
                  <Spinner animation="border" />
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm" className={formStyles.clientsTable}>
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>RIF</th>
                          <th>Nombre Comercial</th>
                          <th>Razón Social</th>
                          <th>Teléfono</th>
                          <th>Email</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.map(c => (
                          <tr
                            key={c.id}
                            className={`${formStyles.selectableRow} ${
                              selectedClient?.id === c.id ? 'table-active' : ''
                            }`}
                            onClick={() => {
                              setSelectedClient(c);
                              applyClientCreditDefaults(c);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedClient(c);
                                applyClientCreditDefaults(c);
                              }
                            }}
                          >
                            <td>{(c as any).code}</td>
                            <td>{c.rif}</td>
                            <td>{c.businessName}</td>
                            <td>{c.legalName}</td>
                            <td>{c.mobile}</td>
                            <td>{c.email}</td>
                            <td>
                              {selectedClient?.id === c.id ? (
                                <span className="badge bg-success">
                                  Seleccionado
                                </span>
                              ) : (
                                <span className="text-muted small">
                                  Seleccionar
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {step === 3 && (
          <>
            {/* Summary of selected Client and Products */}
            <Row className="mb-3 g-3">
              <Col md={5}>
                <div className={`${formStyles.stepThreeSection} h-100`}>
                  <div
                    className={`${formStyles.stepThreeSectionHeader} d-flex justify-content-between align-items-start gap-2`}
                  >
                    <h6 className="mb-0">Cliente seleccionado</h6>
                    {!isProfitSyncRepair && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setStep(2);
                          if (clients.length === 0) fetchClients();
                        }}
                        title="Cambiar cliente"
                      >
                        Cambiar
                      </Button>
                    )}
                  </div>
                  <div className={formStyles.stepThreeSectionBody}>
                    {selectedClient ? (
                      <>
                        <div className={formStyles.clientPrimaryBlock}>
                          <div className={formStyles.clientName}>
                            {selectedClient.businessName ||
                              selectedClient.legalName ||
                              'Cliente sin nombre'}
                          </div>
                          <div className={formStyles.clientMeta}>
                            <span>
                              <strong>Código:</strong>{' '}
                              {(selectedClient as any).code || '-'}
                            </span>
                            <span>
                              <strong>RIF:</strong> {selectedClient.rif || '-'}
                            </span>
                          </div>
                        </div>

                        <div className={formStyles.clientQuickGrid}>
                          <div className={formStyles.clientQuickItem}>
                            <div className={formStyles.clientQuickLabel}>
                              Teléfono
                            </div>
                            <div className={formStyles.clientQuickValue}>
                              {selectedClient.mobile || '-'}
                            </div>
                          </div>
                          <div className={formStyles.clientQuickItem}>
                            <div className={formStyles.clientQuickLabel}>
                              Email
                            </div>
                            <div className={formStyles.clientQuickValue}>
                              {selectedClient.email || '-'}
                            </div>
                          </div>
                          <div className={formStyles.clientQuickItem}>
                            <div className={formStyles.clientQuickLabel}>
                              Crédito cliente
                            </div>
                            <div className={formStyles.clientQuickValue}>
                              {selectedClient.creditTerms !== undefined &&
                              selectedClient.creditTerms !== null
                                ? `${selectedClient.creditTerms} días`
                                : '-'}
                            </div>
                          </div>
                          <div className={formStyles.clientQuickItem}>
                            <div className={formStyles.clientQuickLabel}>
                              Límite crédito
                            </div>
                            <div className={formStyles.clientQuickValue}>
                              {selectedClient.creditLimit !== undefined &&
                              selectedClient.creditLimit !== null
                                ? formatMoney(
                                    Number(selectedClient.creditLimit),
                                    referenceCurrency || currency
                                  )
                                : '-'}
                            </div>
                          </div>
                        </div>

                        <div className="mt-3">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className={formStyles.moreInfoBtn}
                            onClick={() => setShowClientMoreInfo(prev => !prev)}
                          >
                            {showClientMoreInfo
                              ? 'Ocultar información adicional'
                              : 'Ver más información'}
                          </Button>
                        </div>

                        {showClientMoreInfo && (
                          <div className={`${formStyles.kvGrid} mt-3`}>
                            <div className={formStyles.kvItem}>
                              <span className={formStyles.kvLabel}>
                                Términos crédito (cliente):
                              </span>
                              <span className={formStyles.kvValue}>
                                {selectedClient.creditTerms !== undefined &&
                                selectedClient.creditTerms !== null
                                  ? `${selectedClient.creditTerms} días`
                                  : '-'}
                              </span>
                            </div>
                            <div className={formStyles.kvItem}>
                              <span className={formStyles.kvLabel}>
                                Dirección Fiscal:
                              </span>
                              <span className={formStyles.kvValue}>
                                {selectedClient.fiscalAddress ||
                                  selectedClient.address ||
                                  '-'}
                              </span>
                            </div>
                            <div className={formStyles.kvItem}>
                              <span className={formStyles.kvLabel}>
                                Dirección de Envío:
                              </span>
                              <span className={formStyles.kvValue}>
                                {selectedClient.shippingAddress || '-'}
                              </span>
                            </div>
                            {selectedClient.alternativeShippingAddress && (
                              <div className={formStyles.kvItem}>
                                <span className={formStyles.kvLabel}>
                                  Envío Alternativo:
                                </span>
                                <span className={formStyles.kvValue}>
                                  {selectedClient.alternativeShippingAddress}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted">
                        No hay cliente seleccionado
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col md={7}>
                <div className={`${formStyles.stepThreeSection} h-100`}>
                  <div
                    className={`${formStyles.stepThreeSectionHeader} d-flex justify-content-between align-items-start gap-2`}
                  >
                    <h6 className="mb-0">Productos seleccionados</h6>
                    {!isProfitSyncRepair && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setStep(1);
                        }}
                        title="Agregar más productos"
                      >
                        Agregar más
                      </Button>
                    )}
                  </div>
                  <div className={formStyles.stepThreeSectionBody}>
                    <div
                      className={`table-responsive ${formStyles.productsWrap}`}
                    >
                      <Table
                        hover
                        size="sm"
                        className={`mb-0 align-middle ${formStyles.productsSummaryTable}`}
                      >
                        <thead>
                          <tr>
                            <th>Código</th>
                            <th>Nombre</th>
                            <th className={formStyles.moneyHeader}>Precio</th>
                            <th>Desc. %</th>
                            <th>Impuesto</th>
                            <th>Cant.</th>
                            <th className={formStyles.moneyHeader}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProducts.length > 0 ? (
                            selectedProducts.map(p => {
                              const currentPrice =
                                p.customPrice !== undefined
                                  ? p.customPrice
                                  : Number(p.price) || 0;
                              const totalPrice = computeOrderLineSubtotal(
                                p,
                                orderDiscountPercent
                              );
                              return (
                                <tr key={p.id}>
                                  <td>{p.code}</td>
                                  <td>{p.name}</td>
                                  <td className={formStyles.moneyCellNoWrap}>
                                    {p.customPrice !== undefined ? (
                                      <span className="text-primary fw-bold">
                                        {formatMoney(
                                          currentPrice,
                                          referenceCurrency || currency
                                        )}
                                        <small
                                          className={`text-muted d-block ${formStyles.moneyCellNoWrap}`}
                                        >
                                          (Original:{' '}
                                          {formatMoney(
                                            Number(p.price) || 0,
                                            referenceCurrency || currency
                                          )}
                                          )
                                        </small>
                                      </span>
                                    ) : (
                                      formatMoney(
                                        currentPrice,
                                        referenceCurrency || currency
                                      )
                                    )}
                                  </td>
                                  <td>
                                    {formatNumber(
                                      normalizeDiscountPercent(p.discount)
                                    )}
                                    %
                                  </td>
                                  <td>
                                    {(() => {
                                      const info = getProductTaxInfo(p);
                                      return info.taxName
                                        ? `${info.taxName} (${formatNumber(info.taxRate)}%)`
                                        : 'Sin impuesto';
                                    })()}
                                  </td>
                                  <td>{p.selectedQuantity}</td>
                                  <td className={formStyles.moneyCellNoWrap}>
                                    {formatMoney(
                                      totalPrice,
                                      referenceCurrency || currency
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={7}
                                className="text-center text-muted"
                              >
                                Sin productos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <div className={`${formStyles.stepThreeSection} mb-3`}>
              <div className={formStyles.stepThreeSectionHeader}>
                <h6 className="mb-0">Condiciones comerciales</h6>
              </div>
              <div className={formStyles.stepThreeSectionBody}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Vendedor</Form.Label>
                      <Form.Select
                        value={selectedVendorId}
                        onChange={e =>
                          setSelectedVendorId(e.currentTarget.value)
                        }
                        disabled={vendorPending}
                      >
                        <option value="">
                          {vendorPending
                            ? 'Cargando…'
                            : 'Seleccione un vendedor'}
                        </option>
                        {salesPeople.map(v => (
                          <option key={v.id} value={v.id}>
                            {formatSalesPersonOptionLabel(v)}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Se guarda como referencia de la venta (correo, código o
                        nombre si no hay correo).
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Forma de pago</Form.Label>
                      <Form.Select
                        value={paymentMethod}
                        onChange={e => setPaymentMethod(e.currentTarget.value)}
                        disabled={
                          enabledPaymentMethods.length === 0 ||
                          isProfitSyncRepair
                        }
                      >
                        {enabledPaymentMethods.length === 0 ? (
                          <option value="">
                            Configure formas de pago en la organización
                          </option>
                        ) : (
                          enabledPaymentMethods.map(pm => (
                            <option key={pm.id} value={pm.name}>
                              {pm.name}
                            </option>
                          ))
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="credit-enabled-switch"
                        label="Activar venta a crédito (genera cuenta por cobrar)"
                        checked={creditEnabled}
                        onChange={e => {
                          const nextEnabled = e.currentTarget.checked;
                          setCreditEnabled(nextEnabled);
                          if (!nextEnabled) {
                            setCreditTermsDays('');
                            setCreditDueDate('');
                            return;
                          }
                          const inheritedTerms =
                            getClientCreditTermsDays(selectedClient);
                          const safeTerms = inheritedTerms ?? 30;
                          setCreditTermsDays(String(safeTerms));
                          setCreditDueDate(resolveDueDateFromTerms(safeTerms));
                        }}
                        disabled={isProfitSyncRepair}
                      />
                      <Form.Text className="text-muted">
                        Cuando está activo, la venta se trata como crédito según
                        días/fecha de vencimiento, sin depender de la forma de
                        pago.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <DateField
                      id="order-emission-date"
                      label="Fecha emisión"
                      value={
                        isEditing && order?.created
                          ? dateToInputValue(
                              order.created instanceof Date
                                ? order.created
                                : new Date(order.created as string | number)
                            )
                          : emissionDate
                      }
                      onChange={setEmissionDate}
                      disabled={isEditing || isProfitSyncRepair}
                      groupClassName="mb-3"
                    />
                    {!isEditing ? (
                      <Form.Text className="text-muted d-block mb-3">
                        Afecta reportes y CxC. El inventario se actualiza al
                        registrar la venta.
                      </Form.Text>
                    ) : null}
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Días de crédito (venta)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        value={creditTermsDays}
                        onChange={e => {
                          const next = e.currentTarget.value;
                          if (Number(next) <= 0 && next.trim() !== '') {
                            setCreditEnabled(false);
                            setCreditTermsDays('');
                            setCreditDueDate('');
                            return;
                          }
                          setCreditTermsDays(next);
                        }}
                        disabled={isProfitSyncRepair || !creditEnabled}
                        placeholder="Ej: 30"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <DateField
                      id="order-credit-due-date"
                      label="Fecha de vencimiento"
                      value={creditDueDate}
                      onChange={setCreditDueDate}
                      disabled={isProfitSyncRepair || !creditEnabled}
                      groupClassName="mb-3"
                    />
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="mb-3"
                      disabled={!selectedClient || isProfitSyncRepair}
                      onClick={() => applyClientCreditDefaults(selectedClient)}
                    >
                      Heredar crédito del cliente
                    </Button>
                  </Col>
                </Row>

                {showSaleProfitPlusFields && missingProfitVendedorForExport && (
                  <Alert variant="warning" className="mb-3 py-2 small">
                    <strong>Vendedor sin código ERP (co_ven).</strong> El
                    vendedor elegido no está vinculado a Profit Plus en su ficha
                    de MayorX. La venta{' '}
                    <strong>no se exportará al ERP ni tras aprobar</strong>{' '}
                    hasta que asigne un vendedor con <code>co_ven</code>{' '}
                    sincronizado o actualice el registro del vendedor en
                    Configuración.
                  </Alert>
                )}

                {showSaleProfitPlusFields && !hasAnySaleProfitField && (
                  <Alert variant="info" className="mb-3 py-2 small">
                    <strong>Exportación a Profit activa.</strong> Si no completa
                    los campos opcionales de Profit Plus en esta venta, el
                    puente usará solo la configuración de la organización y los
                    valores por defecto del ERP (ignará sobrescrituras por esta
                    venta).
                  </Alert>
                )}

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Moneda de Pago</Form.Label>
                      <Form.Select
                        value={paymentCurrency}
                        onChange={e =>
                          setPaymentCurrency(e.currentTarget.value)
                        }
                        disabled={isProfitSyncRepair}
                      >
                        <option value="">Seleccione una moneda</option>
                        {currency && (
                          <option value={currency}>{currency}</option>
                        )}
                        {referenceCurrency && (
                          <option value={referenceCurrency}>
                            {referenceCurrency}
                          </option>
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {multiWarehouseEnabled ? (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Almacén predeterminado</Form.Label>
                        {warehouseLocked ? (
                          <>
                            <Form.Control
                              readOnly
                              className="bg-light"
                              value={
                                warehouses.find(w => w.id === warehouseId)
                                  ?.name ||
                                order?.warehouseId ||
                                warehouseId
                              }
                            />
                            <Form.Text className="text-muted">
                              El almacén queda fijado al aprobar la venta.
                            </Form.Text>
                          </>
                        ) : (
                          <>
                            <Form.Select
                              value={warehouseId}
                              onChange={e =>
                                setWarehouseId(e.currentTarget.value)
                              }
                              disabled={
                                readOnly ||
                                isProfitSyncRepair ||
                                warehouses.length === 0
                              }
                            >
                              <option value="">Seleccione almacén…</option>
                              {warehouses.map(warehouse => (
                                <option key={warehouse.id} value={warehouse.id}>
                                  {warehouse.name}
                                  {warehouse.isDefault
                                    ? ' (predeterminado)'
                                    : ''}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Se aplica a líneas nuevas; cada producto puede
                              usar otro almacén.
                            </Form.Text>
                          </>
                        )}
                      </Form.Group>
                    </Col>
                  ) : null}
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="apply-iva-switch"
                        label="Aplicar impuestos"
                        checked={applyIva}
                        onChange={e => setApplyIva(e.currentTarget.checked)}
                        disabled={isProfitSyncRepair}
                      />
                      {applyIva && (
                        <Form.Text className="text-muted">
                          Se aplican impuestos por producto según su
                          configuración.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Descuento general (%)</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={orderDiscountPercent}
                        onChange={e =>
                          setOrderDiscountPercent(
                            normalizeDiscountPercent(e.currentTarget.value)
                          )
                        }
                        disabled={warehouseLocked || isProfitSyncRepair}
                      />
                      <Form.Text className="text-muted">
                        Se aplica después del descuento por línea.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Comentarios</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={comment}
                        onChange={e => setComment(e.currentTarget.value)}
                        disabled={isProfitSyncRepair}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Dirección de entrega para esta venta
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={deliveryAddress}
                        onChange={e =>
                          setDeliveryAddress(e.currentTarget.value)
                        }
                        placeholder="Ingrese la dirección de entrega"
                        disabled={isProfitSyncRepair}
                      />
                      <Form.Text className="text-muted">
                        Puedes editar la dirección del cliente solo para esta
                        venta.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>

            <div className={`${formStyles.stepThreeSection} mb-3`}>
              <div className={formStyles.stepThreeSectionHeader}>
                <h6 className="mb-0">Totales y tipo de cambio</h6>
              </div>
              <div className={formStyles.stepThreeSectionBody}>
                <div className={formStyles.totalsStrip}>
                  <div className="text-muted small">
                    {referenceCurrency}→{currency} · Tasa:{' '}
                    {formatExchangeRate(exchangeRate)}:1
                    {exchangeSourceOverrideId
                      ? ' (override)'
                      : defaultExchangeSourceId
                        ? ' (org)'
                        : ''}
                    {orderDiscountPercent > 0
                      ? ` · Descuento general: ${orderDiscountPercent.toFixed(2)}%`
                      : ''}
                  </div>
                  <div className="d-flex flex-wrap gap-3">
                    <div className={formStyles.totalItem}>
                      <strong>Total ({currency}):</strong>{' '}
                      {formatMoney(totals.total * exchangeRate, currency)}
                    </div>
                  </div>
                </div>

                {(isEditing || (referenceCurrency && currency)) && (
                  <Form.Group className="mt-3">
                    <Form.Label>Tipo de cambio para esta venta</Form.Label>
                    <div className="d-flex flex-column flex-md-row gap-2 mb-2">
                      <Form.Select
                        disabled={isProfitSyncRepair}
                        value={exchangeRateMode}
                        onChange={e => {
                          const selectedMode = e.currentTarget
                            .value as ExchangeRateMode;
                          setExchangeRateMode(selectedMode);

                          if (selectedMode === 'saleDefault') {
                            if (originalSaleExchangeRate === null) return;
                            setExchangeSourceOverrideId('');
                            setExchangeRate(originalSaleExchangeRate);
                            return;
                          }

                          if (selectedMode === 'orgDefault') {
                            setExchangeSourceOverrideId('');
                            setExchangeRate(orgEffectiveExchangeRate);
                            return;
                          }

                          if (selectedMode === 'manual') {
                            setExchangeSourceOverrideId('');
                          }
                        }}
                      >
                        {isEditing && (
                          <option
                            value="saleDefault"
                            disabled={originalSaleExchangeRate === null}
                          >
                            Por defecto de la venta
                          </option>
                        )}
                        <option value="orgDefault">
                          Automatica de la organizacion
                        </option>
                        <option value="source">Desde una fuente</option>
                        <option value="manual">Manual</option>
                      </Form.Select>

                      <div className="text-muted small align-self-center">
                        Tasa activa:{' '}
                        <strong>{formatExchangeRate(exchangeRate)}:1</strong>
                      </div>
                    </div>

                    {exchangeRateMode === 'source' && (
                      <Form.Select
                        disabled={isProfitSyncRepair}
                        value={exchangeSourceOverrideId}
                        onChange={e => {
                          const selectedId = e.currentTarget.value;
                          setExchangeSourceOverrideId(selectedId);
                          if (!selectedId) return;
                          const src =
                            exchangeSources.find(s => s.id === selectedId) ??
                            null;
                          if (src && typeof src.rate === 'number') {
                            setExchangeRate(src.rate);
                          }
                        }}
                      >
                        <option value="">Seleccione una fuente</option>
                        {exchangeSources.map(src => (
                          <option key={src.id} value={src.id}>
                            {(src.name && src.name.trim().length > 0
                              ? src.name
                              : src.sourceUrl || src.id) +
                              (typeof src.rate === 'number'
                                ? ` (rate: ${formatExchangeRate(src.rate)})`
                                : '')}
                          </option>
                        ))}
                      </Form.Select>
                    )}

                    {exchangeRateMode === 'manual' && (
                      <Form.Control
                        disabled={isProfitSyncRepair}
                        type="number"
                        min="0"
                        step="0.0001"
                        value={exchangeRate}
                        onChange={e => {
                          const value = Number(e.currentTarget.value);
                          if (!Number.isFinite(value) || value <= 0) return;
                          setExchangeRate(value);
                        }}
                        placeholder="Ingrese la tasa manual"
                      />
                    )}

                    <Form.Text className="text-muted">
                      {exchangeRateMode === 'saleDefault' &&
                        originalSaleExchangeRate !== null &&
                        `Usando tasa original: ${formatExchangeRate(originalSaleExchangeRate)}:1`}
                      {exchangeRateMode === 'orgDefault' &&
                        `Usando tasa de la organización: ${formatExchangeRate(
                          orgEffectiveExchangeRate
                        )}:1${
                          orgAutomaticExchangeSource
                            ? ` (${orgAutomaticExchangeSource.name || 'fuente automática'})`
                            : ''
                        }`}
                      {exchangeRateMode === 'source' &&
                        'Selecciona una fuente para aplicar su tasa.'}
                      {exchangeRateMode === 'manual' &&
                        'Escribe una tasa manual para esta venta.'}
                    </Form.Text>
                  </Form.Group>
                )}
              </div>
            </div>

            {showSaleProfitPlusFields && (
              <Card className="mt-3 mb-1 border-secondary shadow-sm">
                <Card.Header className="py-2 small d-flex align-items-center justify-content-between">
                  Profit Plus 2k8 — Vendedor ERP y resumen del pedido
                </Card.Header>
                <Card.Body className="py-2">
                  <p className="text-muted small mb-2">
                    La sucursal y las condiciones del pedido (moneda USD, tasa,
                    transacción, forma de pago) se definieron en el paso 1. El
                    código de vendedor ERP (<strong>co_ven</strong>) sale del{' '}
                    <strong>Vendedor</strong> elegido arriba (ficha MayorX ↔
                    Profit).
                  </p>
                  <div className="small mb-0 p-3 bg-body-secondary rounded">
                    <div className="mb-1">
                      <strong>Sucursal (co_sucu):</strong>{' '}
                      {profitPedidoSummaryLegible.sucursal}
                    </div>
                    <div className="mb-1">
                      <strong>Moneda (moneda):</strong>{' '}
                      {profitPedidoSummaryLegible.moneda}
                    </div>
                    <div className="mb-1">
                      <strong>Tasa (venta):</strong>{' '}
                      {formatExchangeRate(exchangeRate)}
                    </div>
                    <div className="mb-1">
                      <strong>Transportista (co_tran):</strong>{' '}
                      {profitPedidoSummaryLegible.transportista}
                    </div>
                    <div className="mb-1">
                      <strong>Condición de pago (forma_pag):</strong>{' '}
                      {profitPedidoSummaryLegible.condicion}
                    </div>
                    <div className="mb-1">
                      <strong>Vendedor ERP (co_ven):</strong>{' '}
                      {selectedVendorId === 'admin' ? (
                        profitPedidoSummaryLegible.vendedorPrimary
                      ) : resolvedSalesPersonCoVenForProfit ? (
                        <>
                          {profitPedidoSummaryLegible.vendedorPrimary}
                          {profitPedidoSummaryLegible.vendedorMayorxSubline ? (
                            <span className="text-muted d-block small mt-1">
                              En MayorX:{' '}
                              {profitPedidoSummaryLegible.vendedorMayorxSubline}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <span className="text-warning">
                            sin código — no exporta a Profit
                          </span>
                          {selectedSalesPerson ? (
                            <span className="text-muted d-block small mt-1">
                              En MayorX:{' '}
                              {formatSalesPersonOptionLabel(
                                selectedSalesPerson
                              )}
                            </span>
                          ) : null}
                        </>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => setStep(1)}
                    >
                      Editar en paso 1 (productos)
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'var(--bs-body-bg)',
          zIndex: 1,
        }}
      >
        <div className={formStyles.modalFooterTotals}>
          <div className={formStyles.modalFooterTotalsRow}>
            <span className={formStyles.modalFooterTotalsLabel}>SubTotal</span>
            <span className={formStyles.footerCurrencyStack}>
              <span className={formStyles.modalFooterTotalsValue}>
                {formatMoney(totals.subtotal, referenceCurrency || currency)}
              </span>
              {(currency || '').toUpperCase() === 'VES' &&
                (referenceCurrency || '').toUpperCase() !== 'VES' && (
                  <span className={formStyles.footerCurrencySecondary}>
                    {formatMoney(totals.subtotal * exchangeRate, currency)}
                  </span>
                )}
            </span>
          </div>
          {applyIva && (
            <div className={formStyles.modalFooterTotalsRow}>
              <span className={formStyles.modalFooterTotalsLabel}>
                Impuestos
              </span>
              <span className={formStyles.footerCurrencyStack}>
                <span className={formStyles.modalFooterTotalsValue}>
                  {formatMoney(totals.ivaAmount, referenceCurrency || currency)}
                </span>
                {(currency || '').toUpperCase() === 'VES' &&
                  (referenceCurrency || '').toUpperCase() !== 'VES' && (
                    <span className={formStyles.footerCurrencySecondary}>
                      {formatMoney(totals.ivaAmount * exchangeRate, currency)}
                    </span>
                  )}
              </span>
            </div>
          )}
          <div className={formStyles.modalFooterTotalsRow}>
            <span className={formStyles.modalFooterTotalsLabel}>Total</span>
            <span className={formStyles.footerCurrencyStack}>
              <span className={`${formStyles.modalFooterTotalsValue} fw-bold`}>
                {formatMoney(totals.total, referenceCurrency || currency)}
              </span>
              {(currency || '').toUpperCase() === 'VES' &&
                (referenceCurrency || '').toUpperCase() !== 'VES' && (
                  <span className={formStyles.footerCurrencySecondary}>
                    {formatMoney(totals.total * exchangeRate, currency)}
                  </span>
                )}
            </span>
          </div>
        </div>
        <div className={formStyles.modalFooterActions}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          {step > 1 && (
            <Button variant="outline-primary" onClick={() => setStep(step - 1)}>
              Atrás
            </Button>
          )}
          {step < 3 && (
            <Button
              variant="primary"
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !canProceedStep1) ||
                (step === 2 && !canProceedStep2)
              }
            >
              Siguiente
            </Button>
          )}
          {isProfitSyncRepair && (
            <Button
              variant="success"
              onClick={handleProfitRepairSaveConfirm}
              disabled={
                !selectedVendorId ||
                !paymentMethod ||
                enabledPaymentMethods.length === 0
              }
            >
              Guardar datos Profit
            </Button>
          )}
          {!isProfitSyncRepair && step === 3 && (
            <>
              {approvalReviewMode && isEditing && (
                <Button
                  variant="success"
                  onClick={handleApproveReviewConfirm}
                  disabled={
                    !orderServerHydrated ||
                    !stockBalancesReadyForPersist ||
                    !selectedVendorId ||
                    !paymentMethod ||
                    enabledPaymentMethods.length === 0
                  }
                >
                  Aprobar venta
                </Button>
              )}
              {!(approvalReviewMode && isEditing) && (
                <Button
                  variant="primary"
                  onClick={handleSaveSale}
                  disabled={
                    (isEditing && !orderServerHydrated) ||
                    !stockBalancesReadyForPersist ||
                    !selectedVendorId ||
                    !paymentMethod ||
                    enabledPaymentMethods.length === 0
                  }
                >
                  {isEditing ? 'Guardar Cambios' : 'Crear Venta'}
                </Button>
              )}
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderForm;
